import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  Firestore,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from '@angular/fire/firestore';
import {
  NbDialogService,
  NbToastrService,
} from '@nebular/theme';


import jsPDF from 'jspdf';

export type BlFieldType =
  | 'field'
  | 'text'
  | 'company-logo'
  | 'digital-stamp'
  | 'company-name'
  | 'company-address'
  | 'company-contact'
  | 'container-table';

export type BlViewMode = 'original' | 'non-negotiable';

// Both presentations belong to the SAME saved template/document,
// but each presentation owns an independent editable layout.
export interface BlTemplateViews {
  original: { borderless: true };
  nonNegotiable: { borderless: false };
}

export interface BlViewLayout {
  pageWidthPx: number;
  pageHeightPx: number;
  globalOffsetX: number;
  globalOffsetY: number;
  backgroundImageUrl: string;
  backgroundColor: string;
  textColor: string;
  scanFileName?: string;
  fields: Record<string, BlFieldConfig>;
}

export interface BlTemplateViewLayouts {
  original: BlViewLayout;
  nonNegotiable: BlViewLayout;
}


export type BlFontWeight = 'normal' | 'bold';

export type BlFontFamily =
  | 'helvetica'
  | 'times'
  | 'courier';


export type BlTextAlign =
  | 'left'
  | 'center'
  | 'right'
  | 'justify';

export type BlFontStyle =
  | 'normal'
  | 'italic';

export type BlTextDecoration =
  | 'none'
  | 'underline'
  | 'line-through';

export type BlBorderSide =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left';

export type EditableBlFieldProperty =
  | 'label'
  | 'previewValue'
  | 'dataKey'
  | 'x'
  | 'y'
  | 'width'
  | 'height'
  | 'fontSize'
  | 'fontFamily'
  | 'fontWeight'
  | 'fontStyle'
  | 'textDecoration'
  | 'textAlign'
  | 'lineHeight'
  | 'borderTopWidth'
  | 'borderRightWidth'
  | 'borderBottomWidth'
  | 'borderLeftWidth'
  | 'showLabel'
  | 'visible'
  | 'locked';


export interface BlFieldConfig {
  key: string;
  displayName: string;
  type: BlFieldType;

  label: string;
  dataKey: string;
  previewValue: string;

  x: number;
  y: number;
  width: number;
  height: number;

  fontSize: number;
  fontFamily?: BlFontFamily;
  fontWeight: BlFontWeight;
  textAlign: BlTextAlign;

  lineHeight?: number;
  /** Per-field content padding. Imported OCR fields use a tiny value so small text is never clipped. */
  contentPadding?: number;

  // Repeatable container table settings.
  containerTableShowHeader?: boolean;
  containerTableShowBorders?: boolean;
  containerTableHeaderHeight?: number;
  containerTableRowHeight?: number;
  containerTableColumns?: Array<{
    key: 'containerNo' | 'sealNo' | 'shipmentType' | 'grossWeight' | 'netWeight' | 'packages' | 'cbm';
    label: string;
    width: number;
  }>;

  /**
   * Exact-source visual mode for static imported captions/headings. The whole
   * uploaded page is stored once on the template as sourceTraceImageUrl; each
   * static field stores only its source crop rectangle.
   */
  sourceVisual?: boolean;
  sourceCropX?: number;
  sourceCropY?: number;
  sourceCropWidth?: number;
  sourceCropHeight?: number;

  fontStyle?: BlFontStyle;
  textDecoration?: BlTextDecoration;

  /* Imported forms may contain filled heading cells (for example black bars). */
  backgroundColor?: string;
  textColor?: string;

  showLabel: boolean;
  visible: boolean;
  locked: boolean;

  /*
   * Legacy border value.
   * Old templates isi field ko use karte hain.
   */
  borderWidth: number;

  borderTopWidth?: number;
  borderRightWidth?: number;
  borderBottomWidth?: number;
  borderLeftWidth?: number;

  zIndex: number;
}

export interface BlTemplate {
  id: string;
  companyId: string;
  templateName: string;

  pageWidthPx: number;
  pageHeightPx: number;

  globalOffsetX: number;
  globalOffsetY: number;

  backgroundImageUrl: string;

  /** Hidden source/tracing image. Never rendered as a full-page background. */
  sourceTraceImageUrl?: string;

  backgroundColor: string;
  textColor: string;


  isDefault: boolean;
  isActive: boolean;

  // Legacy / compatibility copy. Always mirrors the Non-Negotiable layout.
  fields: Record<string, BlFieldConfig>;

  // Canonical editable layouts. Both live in this ONE Firestore document.
  viewLayouts?: BlTemplateViewLayouts;

  views?: BlTemplateViews;
  defaultViewMode?: BlViewMode;

  createdAt?: any;
  updatedAt?: any;
}

interface CompanyProfile {
  id: string;
  name: string;
  address: string;
  email: string;
  telephone: string;
  ntn: string;
  logo: string;
}

interface DragState {
  key: string;
  startClientX: number;
  startClientY: number;
  originalX: number;
  originalY: number;
}

type ResizeDirection =
  | 'n'
  | 'ne'
  | 'e'
  | 'se'
  | 's'
  | 'sw'
  | 'w'
  | 'nw';

interface ResizeState {
  key: string;
  direction: ResizeDirection;

  startClientX: number;
  startClientY: number;

  originalX: number;
  originalY: number;

  originalWidth: number;
  originalHeight: number;
}

interface ImportedBlBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;

  borderTop: number;
  borderRight: number;
  borderBottom: number;
  borderLeft: number;

  text?: string;

  // Runtime extraction metadata. These values are not persisted separately;
  // they are used to build the final BlFieldConfig.
  paddingTop?: number;
  paddingRight?: number;
  paddingBottom?: number;
  paddingLeft?: number;

  // Filled section bars (for example SHIP FROM / SHIP TO) are not ordinary cells.
  // Keep this runtime marker so sanitization/OCR never drops them just because
  // they have no four-edge border rectangle.
  filledBand?: boolean;
}

interface ImportedBlStructureSegment {
  orientation: 'horizontal' | 'vertical';
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  thickness: number;
}

interface ImportedBlImageRegion {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

interface BlDataKeyPaletteItem {
  key: string;
  dataKey: string;
  label: string;
  category: string;
  type?: BlFieldType;
}

interface BlOcrReviewSummary {
  mapped: number;
  staticTexts: number;
  unmapped: number;
  lowConfidence: number;
  averageConfidence: number;
  recognizedCharacters: number;
}

@Component({
  selector: 'ngx-bl-template',
  templateUrl: './bl-template.component.html',
  styleUrls: ['./bl-template.component.scss'],
})
export class BlTemplateComponent implements OnInit, OnDestroy {

downloadingTemplateId: string | null = null;


  readonly collectionName = 'bl_template_designs';
  // Small metadata documents: full B/L backgrounds are NEVER loaded for listing.
  readonly templateIndexCollectionName = 'bl_template_designs_index';
  openingTemplateId: string | null = null;
  private templateOpenRequest = 0;

readonly fixedFooterText =
  'Report generated by Portus ERP';

  loading = true;
  saving = false;

  companyId = '';

  templates: BlTemplate[] = [];
  selectedTemplate: BlTemplate | null = null;

  company: CompanyProfile = {
    id: '',
    name: '',
    address: '',
    email: '',
    telephone: '',
    ntn: '',
    logo: '',
  };

  form: Partial<BlTemplate> = this.getEmptyForm();

  fields: Record<string, BlFieldConfig> = this.getDefaultFields();

  dragging: DragState | null = null;
  activeFieldKey: string | null = null;

  readonly fontFamilyOptions: Array<{
    value: BlFontFamily;
    label: string;
  }> = [
    { value: 'helvetica', label: 'Helvetica / Arial' },
    { value: 'times', label: 'Times New Roman' },
    { value: 'courier', label: 'Courier New' },
  ];

  private autoAlignUndoSnapshot:
    Record<string, Pick<BlFieldConfig, 'x' | 'y'>> | null = null;

  resizing: ResizeState | null = null;

readonly resizeDirections: ResizeDirection[] = [
  'nw',
  'n',
  'ne',
  'e',
  'se',
  's',
  'sw',
  'w',
];

  showAddFieldForm = false;

newField = {
  type: 'field' as 'field' | 'text' | 'digital-stamp',

  name: '',
  heading: '',
  previewValue: '',
  dataKey: '',

  showLabel: true,
};

// Manual data-key placement flow. Uploaded PDF/image becomes the locked visual
// template; the user decides the exact field position by drag/drop.
dataKeySearch = '';
private paletteAutoIndex = 0;

// Manual background flow: keep heavy source bytes OUT of Angular state/Firestore.
// A lightweight blob URL is used for preview; the blob is uploaded only on Save.
private pendingBackgroundBlob: Blob | null = null;
private pendingBackgroundObjectUrl = '';
private pendingBackgroundFileName = '';
private pendingBackgroundMimeType = '';


/*
 * PERFORMANCE:
 * Firestore keeps the full-quality Base64 background, but the live designer
 * uses a smaller in-memory preview so browser scrolling does not repaint the
 * 300/400-DPI image on every frame.
 */
private editorBackgroundPreviewUrl = '';
private editorPreviewGeneration = 0;

private pageStyleCache: Record<string, string> = {};
private pageStyleCacheKey = '';

private fieldStyleCache = new Map<
  string,
  {
    field: BlFieldConfig;
    offsetX: number;
    offsetY: number;
    original: boolean;
    textColor: string;
    style: Record<string, string>;
  }
>();

private dataKeyPaletteCacheSearch = '';
private dataKeyPaletteCache: BlDataKeyPaletteItem[] | null = null;

private invalidateRenderCaches(): void {
  this.pageStyleCacheKey = '';
  this.pageStyleCache = {};
  this.fieldStyleCache.clear();
}

private releaseEditorBackgroundPreview(): void {
  this.editorPreviewGeneration += 1;

  if (this.editorBackgroundPreviewUrl) {
    try {
      URL.revokeObjectURL(this.editorBackgroundPreviewUrl);
    } catch {}

    this.editorBackgroundPreviewUrl = '';
  }

  this.invalidateRenderCaches();
}

private async createEditorBackgroundPreviewUrl(
  blob: Blob,
  maxWidth = 850
): Promise<string> {
  let bitmap: ImageBitmap | null = null;

  try {
    bitmap = await createImageBitmap(blob);

    const sourceWidth = Math.max(1, bitmap.width);
    const sourceHeight = Math.max(1, bitmap.height);

    const targetWidth = Math.max(
      1,
      Math.min(maxWidth, sourceWidth)
    );

    const targetHeight = Math.max(
      1,
      Math.round(
        sourceHeight * targetWidth / sourceWidth
      )
    );

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext(
      '2d',
      { alpha: false }
    );

    if (!context) {
      throw new Error(
        'Editor preview canvas is unavailable.'
      );
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.fillStyle = '#ffffff';
    context.fillRect(
      0,
      0,
      targetWidth,
      targetHeight
    );

    context.drawImage(
      bitmap,
      0,
      0,
      targetWidth,
      targetHeight
    );

    const previewBlob =
      await this.canvasToBlobAsync(
        canvas,
        'image/jpeg',
        0.86
      );

    canvas.width = 1;
    canvas.height = 1;

    return URL.createObjectURL(previewBlob);
  } finally {
    try {
      bitmap?.close();
    } catch {}
  }
}

private async useEditorPreviewFromBlob(
  blob: Blob
): Promise<void> {
  const generation =
    ++this.editorPreviewGeneration;

  try {
    const nextUrl =
      await this.createEditorBackgroundPreviewUrl(
        blob
      );

    if (generation !== this.editorPreviewGeneration) {
      try {
        URL.revokeObjectURL(nextUrl);
      } catch {}
      return;
    }

    if (this.editorBackgroundPreviewUrl) {
      try {
        URL.revokeObjectURL(
          this.editorBackgroundPreviewUrl
        );
      } catch {}
    }

    this.editorBackgroundPreviewUrl = nextUrl;
    this.invalidateRenderCaches();
  } catch (error) {
    console.warn(
      '[BL PERF] Lightweight editor preview could not be created.',
      error
    );
  }
}

private async refreshEditorPreviewFromSavedBackground(): Promise<void> {
  const source = String(
    this.viewLayouts?.nonNegotiable?.backgroundImageUrl || ''
  ).trim();

  if (!source) {
    this.releaseEditorBackgroundPreview();
    return;
  }

  const generation =
    ++this.editorPreviewGeneration;

  try {
    const response = await fetch(source);
    const blob = await response.blob();

    const nextUrl =
      await this.createEditorBackgroundPreviewUrl(
        blob
      );

    if (generation !== this.editorPreviewGeneration) {
      try {
        URL.revokeObjectURL(nextUrl);
      } catch {}
      return;
    }

    if (this.editorBackgroundPreviewUrl) {
      try {
        URL.revokeObjectURL(
          this.editorBackgroundPreviewUrl
        );
      } catch {}
    }

    this.editorBackgroundPreviewUrl = nextUrl;
    this.invalidateRenderCaches();
  } catch (error) {
    console.warn(
      '[BL PERF] Saved background preview optimization skipped.',
      error
    );
  }
}

private releasePendingBackgroundObjectUrl(): void {
  if (this.pendingBackgroundObjectUrl) {
    try { URL.revokeObjectURL(this.pendingBackgroundObjectUrl); } catch {}
    this.pendingBackgroundObjectUrl = '';
  }
}


/**
 * TEST MODE:
 * Convert the pending background Blob/File into a data URL so the complete
 * image is persisted directly inside the Firestore template document.
 *
 * Example result:
 * data:image/jpeg;base64,/9j/4AAQSk...
 */
private blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || '');

      if (!result.startsWith('data:')) {
        reject(new Error('Could not convert B/L background to Base64.'));
        return;
      }

      resolve(result);
    };

    reader.onerror = () => {
      reject(
        reader.error ||
        new Error('Could not read B/L background.')
      );
    };

    reader.readAsDataURL(blob);
  });
}


@ViewChild('templateViewDialog', {
  static: false,
})
templateViewDialog!: TemplateRef<any>;

// ONE Firestore template, TWO completely independent editable layouts.
viewMode: BlViewMode = 'non-negotiable';

private viewLayouts: BlTemplateViewLayouts | null = null;

// Performance: avoid rebuilding the large default field map for every normalization.
private defaultFieldsCache: Record<string, BlFieldConfig> | null = null;

scanProcessing = false;
scanProcessingProgress = 0;
scanProcessingStatus = '';
nonNegotiableScanFileName = '';

// OCR import/review is intentionally isolated from the normal designer.
// Heavy PDF/OCR libraries are lazy-loaded only after the user selects a file.
ocrReviewVisible = false;
ocrReviewSourceImage = '';
ocrReviewGeneratedFields: Record<string, BlFieldConfig> = {};
ocrReviewGeneratedLayouts: BlTemplateViewLayouts | null = null;
ocrReviewFileName = '';
ocrReviewAttempt = 0;
ocrReviewSourceMode: 'native-pdf' | 'ocr' = 'ocr';
ocrReviewSummary: BlOcrReviewSummary = {
  mapped: 0,
  staticTexts: 0,
  unmapped: 0,
  lowConfidence: 0,
  averageConfidence: 0,
  recognizedCharacters: 0,
};

ocrReviewConfidence: Record<
  string,
  { confidence: number; mapped: boolean; reason: string }
> = {};

private ocrReviewFile: File | null = null;


setViewMode(mode: BlViewMode): void {
  const nextMode: BlViewMode =
    mode === 'original' ? 'original' : 'non-negotiable';

  if (nextMode === this.viewMode) {
    return;
  }

  // Persist every unsaved edit of the currently visible view in memory first.
  this.commitCurrentViewLayout();

  this.viewMode = nextMode;
  this.activeFieldKey = null;
  this.dragging = null;
  this.resizing = null;

  // Then load the other view's independent page + field configuration.
  this.applyViewLayout(nextMode);
}



private isOriginalView(): boolean {
  return this.viewMode === 'original';
}

private readonly templateViews: BlTemplateViews = {
  original: { borderless: true },
  nonNegotiable: { borderless: false },
};

private isDocumentTypeField(key: string, field?: BlFieldConfig): boolean {
  return key === 'documentType' || field?.dataKey === 'documentType';
}

private getDocumentTypeText(): string {
  return this.viewMode === 'original' ? 'ORIGINAL' : 'NON-NEGOTIABLE';
}

private createViewLayout(
  fields: Record<string, BlFieldConfig>,
  source?: Partial<BlViewLayout>
): BlViewLayout {
  return {
    pageWidthPx: this.toNumber(source?.pageWidthPx, 794),
    pageHeightPx: this.toNumber(source?.pageHeightPx, 1128),
    globalOffsetX: this.toNumber(source?.globalOffsetX, 0),
    globalOffsetY: this.toNumber(source?.globalOffsetY, 0),
    backgroundImageUrl: String(source?.backgroundImageUrl || '').trim(),
    backgroundColor: String(source?.backgroundColor || '#ffffff'),
    textColor: String(source?.textColor || '#111827'),
    scanFileName: String(source?.scanFileName || '').trim(),
    fields: this.normalizeFields(this.clone(fields || {})),
  };
}

private captureCurrentViewLayout(): BlViewLayout {
  return this.createViewLayout(
    this.fields,
    {
      pageWidthPx: this.currentPageWidth,
      pageHeightPx: this.currentPageHeight,
      globalOffsetX: this.toNumber(this.form.globalOffsetX, 0),
      globalOffsetY: this.toNumber(this.form.globalOffsetY, 0),
      backgroundImageUrl: String(this.form.backgroundImageUrl || '').trim(),
      backgroundColor: String(this.form.backgroundColor || '#ffffff'),
      textColor: String(this.form.textColor || '#111827'),
      scanFileName:
        this.viewMode === 'non-negotiable'
          ? this.nonNegotiableScanFileName
          : '',
    }
  );
}

private commitCurrentViewLayout(): void {
  if (!this.viewLayouts) {
    return;
  }

  const current = this.captureCurrentViewLayout();

  if (this.viewMode === 'original') {
    this.viewLayouts.original = current;
  } else {
    this.viewLayouts.nonNegotiable = current;
  }
}

private applyViewLayout(mode: BlViewMode): void {
  if (!this.viewLayouts) {
    return;
  }

  const layout =
    mode === 'original'
      ? this.viewLayouts.original
      : this.viewLayouts.nonNegotiable;

  this.form.pageWidthPx = layout.pageWidthPx;
  this.form.pageHeightPx = layout.pageHeightPx;
  this.form.globalOffsetX = layout.globalOffsetX;
  this.form.globalOffsetY = layout.globalOffsetY;
  this.form.backgroundImageUrl = layout.backgroundImageUrl;
  this.form.backgroundColor = layout.backgroundColor;
  this.form.textColor = layout.textColor;

  if (mode === 'non-negotiable') {
    this.nonNegotiableScanFileName = String(layout.scanFileName || '').trim();
  }

  // Deep clone is critical: editing/deleting in one view must never mutate the other.
  this.fields = this.enforceFixedFooter(
    this.clone(layout.fields || {})
  );

  this.invalidateRenderCaches();
}

private normalizeViewLayout(
  savedLayout: any,
  fallbackFields: Record<string, BlFieldConfig>,
  fallbackPage: Partial<BlViewLayout>
): BlViewLayout {
  const sourceFields =
    savedLayout?.fields &&
    Object.keys(savedLayout.fields).length
      ? savedLayout.fields
      : fallbackFields;

  return this.createViewLayout(
    sourceFields,
    {
      pageWidthPx: savedLayout?.pageWidthPx ?? fallbackPage.pageWidthPx,
      pageHeightPx: savedLayout?.pageHeightPx ?? fallbackPage.pageHeightPx,
      globalOffsetX: savedLayout?.globalOffsetX ?? fallbackPage.globalOffsetX,
      globalOffsetY: savedLayout?.globalOffsetY ?? fallbackPage.globalOffsetY,
      backgroundImageUrl:
        savedLayout?.backgroundImageUrl ?? fallbackPage.backgroundImageUrl,
      backgroundColor:
        savedLayout?.backgroundColor ?? fallbackPage.backgroundColor,
      textColor:
        savedLayout?.textColor ?? fallbackPage.textColor,
      scanFileName:
        savedLayout?.scanFileName ?? fallbackPage.scanFileName ?? '',
    }
  );
}

private getTemplateLayoutForMode(
  template: BlTemplate,
  mode: BlViewMode
): BlViewLayout {
  const layouts = template.viewLayouts;

  if (layouts) {
    return this.clone(
      mode === 'original'
        ? layouts.original
        : layouts.nonNegotiable
    );
  }

  return this.createViewLayout(
    template.fields || {},
    {
      pageWidthPx: template.pageWidthPx,
      pageHeightPx: template.pageHeightPx,
      globalOffsetX: template.globalOffsetX,
      globalOffsetY: template.globalOffsetY,
      backgroundImageUrl: template.backgroundImageUrl,
      backgroundColor: template.backgroundColor,
      textColor: template.textColor,
    }
  );
}
private createOriginalLayoutFromNonNegotiable(
  sourceLayout: BlViewLayout
): BlViewLayout {
  const originalFields: Record<string, BlFieldConfig> = {};

  /*
   * ORIGINAL B/L RULE:
   * ------------------
   * Original is a clean white/pre-printed-paper output.
   *
   * KEEP:
   *   - ONLY fields that have an ERP dataKey
   *   - exact NON x/y/width/height
   *   - same typography/alignment
   *
   * REMOVE:
   *   - uploaded/background image
   *   - company/source logo
   *   - headings/static text
   *   - grey fills
   *   - lines/borders
   *   - design-only structure
   */
  Object.entries(sourceLayout.fields || {}).forEach(
    ([key, sourceField]) => {
      const dataKey =
        String(sourceField?.dataKey || '').trim();

      // Original contains ONLY actual runtime/data-key fields.
      if (!dataKey) {
        return;
      }

      // Logo must never print on Original.
      if (
        sourceField.type === 'company-logo' ||
        dataKey === 'company.logo'
      ) {
        return;
      }

      // Structure/static decoration is never part of Original.
      if (
        this.isImportedStructureField(
          key,
          sourceField
        )
      ) {
        return;
      }

      const field =
        this.clone(sourceField);

      // Keep exact placement/size from Non-Negotiable.
      field.x = sourceField.x;
      field.y = sourceField.y;
      field.width = sourceField.width;
      field.height = sourceField.height;

      // Original prints values only.
      field.showLabel = false;
      field.backgroundColor = 'transparent';

      field.borderWidth = 0;
      field.borderTopWidth = 0;
      field.borderRightWidth = 0;
      field.borderBottomWidth = 0;
      field.borderLeftWidth = 0;

      originalFields[key] = field;
    }
  );

  return this.createViewLayout(
    originalFields,
    {
      pageWidthPx: sourceLayout.pageWidthPx,
      pageHeightPx: sourceLayout.pageHeightPx,
      globalOffsetX: sourceLayout.globalOffsetX,
      globalOffsetY: sourceLayout.globalOffsetY,

      // Original must always be a clean white page.
      backgroundImageUrl: '',
      backgroundColor: '#ffffff',
      textColor: sourceLayout.textColor || '#111827',
      scanFileName: '',
    }
  );
}

syncOriginalFromNonNegotiable(showToast = true): void {
  this.commitCurrentViewLayout();

  if (!this.viewLayouts) return;

  this.viewLayouts.original =
    this.createOriginalLayoutFromNonNegotiable(
      this.viewLayouts.nonNegotiable
    );

  if (this.viewMode === 'original') {
    this.applyViewLayout('original');
  }

  if (showToast) {
    this.toastr.success(
      'Original layout rebuilt from the Non-Negotiable alignment. You can edit it independently now.',
      'Original Ready'
    );
  }
}

private getDataKeyCategory(key: string, dataKey: string): string {
  const value = `${key} ${dataKey}`.toLowerCase();

  if (value.includes('company')) return 'Company';
  if (/(consignor|consignee|notify|deliverycontact|shipper)/.test(value)) return 'Parties';
  if (/(port|placeof|vessel|voyage|route|transport|deliveryperiod)/.test(value)) return 'Routing';
  if (/(container|goods|package|weight|measurement|cargo)/.test(value)) return 'Cargo';
  if (/(freight|issue|documenttype|originalbl|signatory|blnumber|stamp)/.test(value)) return 'B/L Details';

  return 'Other';
}

get dataKeyPaletteItems(): BlDataKeyPaletteItem[] {
  const search =
    String(this.dataKeySearch || '')
      .trim()
      .toLowerCase();

  if (
    this.dataKeyPaletteCache &&
    this.dataKeyPaletteCacheSearch === search
  ) {
    return this.dataKeyPaletteCache;
  }

  const companyItems: BlDataKeyPaletteItem[] = [
    { key: 'companyLogo', dataKey: 'company.logo', label: 'Company Logo', category: 'Company', type: 'company-logo' },
    { key: 'companyName', dataKey: 'company.name', label: 'Company Name', category: 'Company', type: 'company-name' },
    { key: 'companyAddress', dataKey: 'company.address', label: 'Company Address', category: 'Company', type: 'company-address' },
    { key: 'companyContact', dataKey: 'company.contact', label: 'Company Contact Line', category: 'Company', type: 'company-contact' },
    { key: 'companyPhone', dataKey: 'company.phone', label: 'Company Phone', category: 'Company', type: 'field' },
    { key: 'companyEmail', dataKey: 'company.email', label: 'Company Email', category: 'Company', type: 'field' },
    { key: 'companyNtn', dataKey: 'company.ntn', label: 'Company NTN', category: 'Company', type: 'field' },
  ];

  const canonical = this.getImportDataKeyOptions().map(item => ({
    ...item,
    category: this.getDataKeyCategory(item.key, item.dataKey),
    type: (
      item.key === 'digitalStamp'
        ? 'digital-stamp'
        : 'field'
    ) as BlFieldType,
  }));

  const specialItems: BlDataKeyPaletteItem[] = [
    {
      key: 'containerDetailsTable',
      dataKey: 'containerDetails',
      label: 'Container Details Table',
      category: 'Cargo',
      type: 'container-table',
    },
  ];

  const byDataKey =
    new Map<string, BlDataKeyPaletteItem>();

  [
    ...companyItems,
    ...specialItems,
    ...canonical,
  ].forEach(item => {
    if (!byDataKey.has(item.dataKey)) {
      byDataKey.set(item.dataKey, item);
    }
  });

  const result =
    Array.from(byDataKey.values())
      .filter(
        item =>
          !search ||
          `${item.label} ${item.dataKey} ${item.category}`
            .toLowerCase()
            .includes(search)
      )
      .sort(
        (a, b) =>
          a.category.localeCompare(b.category) ||
          a.label.localeCompare(b.label)
      );

  this.dataKeyPaletteCacheSearch = search;
  this.dataKeyPaletteCache = result;

  return result;
}

private dataKeyDragActive = false;
private dataKeyDragPointerY = 0;
private dataKeyAutoScrollFrame: number | null = null;

private readonly dataKeyDocumentDragOver = (event: DragEvent): void => {
  if (!this.dataKeyDragActive) return;

  this.dataKeyDragPointerY = event.clientY;
  this.ensureDataKeyAutoScroll();
};

private ensureDataKeyAutoScroll(): void {
  if (!this.dataKeyDragActive || this.dataKeyAutoScrollFrame !== null) return;

  const tick = (): void => {
    this.dataKeyAutoScrollFrame = null;
    if (!this.dataKeyDragActive) return;

    const viewportHeight = Math.max(
      1,
      window.innerHeight || document.documentElement.clientHeight || 1
    );

    const edgeZone = Math.min(140, Math.max(80, viewportHeight * 0.14));
    const pointerY = this.dataKeyDragPointerY;

    let delta = 0;

    if (pointerY > 0 && pointerY < edgeZone) {
      const ratio = (edgeZone - pointerY) / edgeZone;
      delta = -Math.max(4, Math.round(30 * ratio));
    } else if (
      pointerY > viewportHeight - edgeZone &&
      pointerY <= viewportHeight + 40
    ) {
      const ratio =
        (pointerY - (viewportHeight - edgeZone)) / edgeZone;
      delta = Math.max(4, Math.round(30 * Math.min(1, ratio)));
    }

    if (delta !== 0) {
      window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
      this.dataKeyAutoScrollFrame = requestAnimationFrame(tick);
    }
  };

  this.dataKeyAutoScrollFrame = requestAnimationFrame(tick);
}

private stopDataKeyAutoScroll(): void {
  this.dataKeyDragActive = false;
  this.dataKeyDragPointerY = 0;

  document.removeEventListener('dragover', this.dataKeyDocumentDragOver, true);

  if (this.dataKeyAutoScrollFrame !== null) {
    cancelAnimationFrame(this.dataKeyAutoScrollFrame);
    this.dataKeyAutoScrollFrame = null;
  }
}

onDataKeyDragStart(event: DragEvent, item: BlDataKeyPaletteItem): void {
  if (!event.dataTransfer) return;

  event.dataTransfer.effectAllowed = 'copy';
  event.dataTransfer.setData(
    'application/x-portus-bl-datakey',
    JSON.stringify(item)
  );
  event.dataTransfer.setData('text/plain', item.dataKey);

  this.stopDataKeyAutoScroll();
  this.dataKeyDragActive = true;
  this.dataKeyDragPointerY = event.clientY;
  document.addEventListener('dragover', this.dataKeyDocumentDragOver, true);
}

onDataKeyDragEnd(): void {
  this.stopDataKeyAutoScroll();
}

onTemplateCanvasDragOver(event: DragEvent): void {
  event.preventDefault();
  this.dataKeyDragPointerY = event.clientY;
  this.ensureDataKeyAutoScroll();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
}

onTemplateCanvasDrop(event: DragEvent): void {
  event.preventDefault();
  this.stopDataKeyAutoScroll();

  const raw = event.dataTransfer?.getData(
    'application/x-portus-bl-datakey'
  );
  if (!raw) return;

  let item: BlDataKeyPaletteItem;
  try {
    item = JSON.parse(raw);
  } catch {
    return;
  }

  const element = event.currentTarget as HTMLElement | null;
  if (!element) return;

  const rect = element.getBoundingClientRect();
  const scaleX = this.currentPageWidth / Math.max(1, rect.width);
  const scaleY = this.currentPageHeight / Math.max(1, rect.height);

  const x = (event.clientX - rect.left) * scaleX;
  const y = (event.clientY - rect.top) * scaleY;

  this.addDataKeyToCanvas(item, x, y);
}

addDataKeyFromPalette(item: BlDataKeyPaletteItem): void {
  const column = this.paletteAutoIndex % 3;
  const row = Math.floor(this.paletteAutoIndex / 3) % 8;
  this.paletteAutoIndex += 1;

  this.addDataKeyToCanvas(
    item,
    35 + column * 170,
    40 + row * 58
  );
}

private addDataKeyToCanvas(
  item: BlDataKeyPaletteItem,
  requestedX: number,
  requestedY: number
): void {
  const defaults = this.getDefaultFields();
  const base = defaults[item.key] ? this.clone(defaults[item.key]) : null;

  const type: BlFieldType = item.type || base?.type || 'field';
  const defaultWidth =
    type === 'company-logo'
      ? 120
      : type === 'container-table'
        ? Math.min(620, Math.max(320, this.currentPageWidth - 70))
        : Math.max(110, Math.min(300, this.toNumber(base?.width, 180)));

  const defaultHeight =
    type === 'company-logo'
      ? 65
      : type === 'container-table'
        ? 150
        : Math.max(24, Math.min(100, this.toNumber(base?.height, 34)));

  const width = Math.min(defaultWidth, Math.max(40, this.currentPageWidth - 4));
  const height = Math.min(defaultHeight, Math.max(20, this.currentPageHeight - 4));
  const x = this.clamp(requestedX - width / 2, 0, Math.max(0, this.currentPageWidth - width), 0);
  const y = this.clamp(requestedY - height / 2, 0, Math.max(0, this.currentPageHeight - height), 0);

  let key = item.key;
  if (this.fields[key]) {
    let index = 2;
    while (this.fields[`${item.key}_${index}`]) index += 1;
    key = `${item.key}_${index}`;
  }

  const field: BlFieldConfig = {
    ...(base || {
      key,
      displayName: item.label,
      type,
      label: item.label,
      dataKey: item.dataKey,
      previewValue: '',
      x: 0,
      y: 0,
      width,
      height,
      fontSize: 9,
      fontFamily: 'helvetica',
      fontWeight: 'normal',
      textAlign: 'left',
      showLabel: false,
      visible: true,
      locked: false,
      borderWidth: 0,
      zIndex: 100,
    }),
    key,
    displayName: item.label,
    type,
    label: '',
    dataKey: item.dataKey,
    previewValue: '',
    x: this.roundOneDecimal(x),
    y: this.roundOneDecimal(y),
    width: this.roundOneDecimal(width),
    height: this.roundOneDecimal(height),
    fontSize: type === 'company-logo'
  ? 9
  : type === 'container-table'
    ? 7.5
    : Math.max(7, Math.min(11, this.toNumber(base?.fontSize, 9))),

    fontFamily:
      base?.fontFamily ||
      'helvetica',

    // Every newly placed ERP dataKey starts NORMAL.
    // User can still press Bold manually afterwards.
    fontWeight: 'normal',

    showLabel: false,
    visible: true,
    locked: false,
    backgroundColor: '',
    contentPadding: type === 'company-logo' ? 0 : 1,
    borderWidth: 0,
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,

    ...(type === 'container-table'
      ? {
          containerTableShowHeader: true,
          containerTableShowBorders: false,
          containerTableHeaderHeight: 22,
          containerTableRowHeight: 22,
          containerTableColumns: [
            { key: 'containerNo', label: 'Container', width: 24 },
            { key: 'sealNo', label: 'Seal', width: 15 },
            { key: 'shipmentType', label: 'Type', width: 12 },
            { key: 'grossWeight', label: 'GR WT', width: 14 },
            { key: 'netWeight', label: 'NT WT', width: 14 },
            { key: 'packages', label: 'PCKGS', width: 16 },
            { key: 'cbm', label: 'CBM', width: 12 },
          ],
        }
      : {}),

    zIndex: 200 + Object.keys(this.fields).length,
  };

  this.keepFieldInsidePage(field);
  this.fields = { ...this.fields, [key]: field };
  this.activeFieldKey = key;

  if (this.viewLayouts) {
    this.commitCurrentViewLayout();
  }
}

getImportDataKeyOptions(): Array<{ key: string; dataKey: string; label: string }> {
  /*
   * These are the paths the actual HouseDetails HBL renderer resolves.
   * Keep this list explicit so imported/custom fields can always choose the
   * same data contract even when a field is synthetic and not in defaults.
   */
  const canonical = [
    { key: 'consignor', dataKey: 'consignor.name', label: 'Shipper / Consignor Name' },
    { key: 'consignorAddress', dataKey: 'consignor.address', label: 'Shipper / Consignor Address' },
    { key: 'consignorFullText', dataKey: 'consignor.fullText', label: 'Shipper / Consignor Full Text' },

    { key: 'consignee', dataKey: 'consignee.name', label: 'Consignee Name' },
    { key: 'consigneeAddress', dataKey: 'consignee.address', label: 'Consignee Address' },
    { key: 'consigneeFullText', dataKey: 'consignee.fullText', label: 'Consignee Full Text' },

    { key: 'notifyPartyName', dataKey: 'notifyParty.name', label: 'Notify Party Name' },
    { key: 'notifyPartyAddress', dataKey: 'notifyParty.address', label: 'Notify Party Address' },
    { key: 'notifyParty', dataKey: 'notifyParty.fullText', label: 'Notify Party Full Text' },

    { key: 'deliveryContact', dataKey: 'deliveryContact', label: 'Delivery Contact' },
    { key: 'deliveryContactAddress', dataKey: 'deliveryContactAddress', label: 'Delivery Contact Address' },
    { key: 'deliveryContactFullText', dataKey: 'deliveryContactFullText', label: 'Delivery Contact Full Text' },

    { key: 'blNumber', dataKey: 'blNumber', label: 'B/L Number' },
    { key: 'placeOfAcceptance', dataKey: 'placeOfAcceptance', label: 'Place of Acceptance / Receipt' },
    { key: 'portOfLoading', dataKey: 'portOfLoading', label: 'Port of Loading' },
    { key: 'portOfDischarge', dataKey: 'portOfDischarge', label: 'Port of Discharge' },
    { key: 'vesselVoyage', dataKey: 'vesselVoyage', label: 'Vessel / Voyage' },
    { key: 'placeOfDelivery', dataKey: 'placeOfDelivery', label: 'Place of Delivery' },
    { key: 'modeOfTransport', dataKey: 'modeOfTransport', label: 'Mode of Transport' },
    { key: 'shipmentType', dataKey: 'shipmentType', label: 'Shipment Type (FCL / LCL)' },
    { key: 'route', dataKey: 'route', label: 'Route / Transshipment' },
    { key: 'deliveryPeriod', dataKey: 'deliveryPeriod', label: 'Delivery Period' },

    { key: 'containerMarks', dataKey: 'containerMarks', label: 'Container / Seal' },
    { key: 'marksNumbers', dataKey: 'marksNumbers', label: 'Marks & Numbers' },
    { key: 'goodsDescription', dataKey: 'goodsDescription', label: 'Description of Goods' },
    { key: 'numberOfPackages', dataKey: 'numberOfPackages', label: 'Number of Packages' },
    { key: 'grossWeight', dataKey: 'grossWeight', label: 'Gross / Net Weight Block' },
    { key: 'grossWeightOnly', dataKey: 'grossWeightOnly', label: 'Gross Weight Only' },
    { key: 'netWeight', dataKey: 'netWeight', label: 'Net Weight' },
    { key: 'measurement', dataKey: 'measurement', label: 'Measurement / CBM' },

    { key: 'freightAmount', dataKey: 'freightAmount', label: 'Freight Prepaid / Collect' },
    { key: 'freightPayableAt', dataKey: 'freightPayableAt', label: 'Freight Payable At' },
    { key: 'originalBlCount', dataKey: 'originalBlCount', label: 'Number of Original B/Ls' },
    { key: 'issuePlace', dataKey: 'issuePlace', label: 'Place of Issue' },
    { key: 'issueDate', dataKey: 'issueDate', label: 'Date of Issue' },
    { key: 'issuePlaceDate', dataKey: 'issuePlaceDate', label: 'Place and Date of Issue' },
    { key: 'documentType', dataKey: 'documentType', label: 'Document Type' },
    { key: 'authorisedSignatory', dataKey: 'authorisedSignatory', label: 'Authorised Signatory' },
    { key: 'digitalStamp', dataKey: 'digitalStamp', label: 'Digital Stamp' },
  ];

  return canonical;
}

private getImportReviewState(): Record<
  string,
  { confidence: number; mapped: boolean; reason: string }
> {
  type ImportReviewHost = {
    __blImportReview?: Record<
      string,
      { confidence: number; mapped: boolean; reason: string }
    >;
  };

  const host = this as unknown as ImportReviewHost;

  if (!host.__blImportReview) {
    host.__blImportReview = {};
  }

  return host.__blImportReview;
}

getImportFieldConfidence(key: string): number {
  return this.getImportReviewState()[key]?.confidence ?? 1;
}

isImportFieldLowConfidence(key: string): boolean {
  const review = this.getImportReviewState()[key];
  return Boolean(review && (!review.mapped || review.confidence < 0.8));
}

getImportReviewSummary(): {
  mapped: number;
  staticTexts: number;
  unmapped: number;
  lowConfidence: number;
} {
  const review = this.getImportReviewState();
  let mapped = 0;
  let unmapped = 0;
  let lowConfidence = 0;

  Object.values(review).forEach(item => {
    if (item.mapped) mapped++;
    else unmapped++;
    if (item.confidence < 0.8) lowConfidence++;
  });

  const staticTexts = Object.values(this.fields).filter(
    field =>
      field.type === 'text' &&
      field.key !== 'footer' &&
      String(
        field.previewValue || ''
      ).trim()
  ).length;

  return { mapped, staticTexts, unmapped, lowConfidence };
}


async onNonNegotiableScanSelected(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input?.files?.[0];

  if (!file) return;

  const name = String(file.name || '').toLowerCase();
  const isPdf = file.type === 'application/pdf' || name.endsWith('.pdf');
  const isImage = file.type.startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(name);

  if (!isPdf && !isImage) {
    this.toastr.warning(
      'Please upload a PDF, PNG, JPG, JPEG or WEBP file.',
      'Unsupported BL File'
    );
    input.value = '';
    return;
  }

  if (file.size > 25 * 1024 * 1024) {
    this.toastr.warning('BL file must be smaller than 25 MB.', 'File Too Large');
    input.value = '';
    return;
  }

  // Manual placement mode does NOT clone the current designer and does NOT run
  // OCR / geometry / AI. Those were the main causes of the browser "Waiting" state.

  this.ocrReviewFile = file;
  this.ocrReviewFileName = file.name;
  this.ocrReviewAttempt = 1;

  try {
    await this.processOcrImport(file, false);
  } finally {
    input.value = '';
  }
}

private async getImagePixelSize(file: Blob): Promise<{ width: number; height: number }> {
  if ('createImageBitmap' in window) {
    const bitmap = await createImageBitmap(file);
    try {
      return {
        width: Math.max(1, bitmap.width),
        height: Math.max(1, bitmap.height),
      };
    } finally {
      bitmap.close?.();
    }
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve({
        width: Math.max(1, image.naturalWidth || image.width),
        height: Math.max(1, image.naturalHeight || image.height),
      });
      image.onerror = () => reject(new Error('Image dimensions could not be read.'));
      image.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

private canvasToBlobAsync(
  canvas: HTMLCanvasElement,
  type = 'image/jpeg',
  quality = 0.9
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('Background image could not be created.')),
      type,
      quality
    );
  });
}

private async prepareManualBackground(file: File): Promise<{
  blob: Blob;
  previewUrl: string;
  pageWidth: number;
  pageHeight: number;
  nativePdf: boolean;
}> {
  const name = String(file.name || '').toLowerCase();
  const isPdf = file.type === 'application/pdf' || name.endsWith('.pdf');

  if (!isPdf) {
    const size = await this.getImagePixelSize(file);
    const pageWidth = 794;
    const pageHeight = Math.max(1, Math.round(pageWidth * size.height / size.width));
    const previewUrl = URL.createObjectURL(file);

    return {
      blob: file,
      previewUrl,
      pageWidth,
      pageHeight,
      nativePdf: false,
    };
  }

  // PDF is rasterized ONCE. No vector scan, no OCR, no text extraction, no deskew.
  const pdfjsLib: any = await import('pdfjs-dist');
  pdfjsLib.GlobalWorkerOptions.workerSrc = 'assets/pdf.worker.min.js';

  const data = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({
    data,
    disableAutoFetch: true,
    disableStream: true,
  }).promise;

  try {
    const page = await pdf.getPage(1);
    const unitViewport = page.getViewport({ scale: 1 });

    // Print-quality background: render the original PDF page at a true
    // 400-DPI equivalent (PDF coordinates are 72 points per inch).
    // Example: A4 width 595pt -> ~3306px, Letter 612pt -> ~3400px.
    // The designer still uses the lightweight 794px logical coordinate system;
    // only the stored background carries the high-resolution pixels.
    /*
     * 220 DPI is still sharp for B/L printing, but dramatically reduces
     * decoded image memory and Base64 size versus 400 DPI.
     *
     * 400 DPI Letter ~= 3400 x 4400 (~60 MB decoded RGBA in the browser).
     * 220 DPI Letter ~= 1870 x 2420 (~18 MB decoded RGBA).
     */
    const printDpi = 220;
    const renderScale = Math.max(1, printDpi / 72);
    const viewport = page.getViewport({ scale: renderScale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(viewport.width));
    canvas.height = Math.max(1, Math.ceil(viewport.height));

    const context = canvas.getContext('2d');
    if (!context) throw new Error('PDF canvas is unavailable.');

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: context, viewport }).promise;
    await this.yieldToBrowser();

    // Keep JPEG compression extremely light so small B/L text and thin rules
    // remain crisp when the final document is printed.
    const blob = await this.canvasToBlobAsync(canvas, 'image/jpeg', 0.90);
    const previewUrl = URL.createObjectURL(blob);
    const pageWidth = 794;
    const pageHeight = Math.max(1, Math.round(pageWidth * unitViewport.height / unitViewport.width));

    // Release backing pixels immediately; browser can GC the canvas memory.
    canvas.width = 1;
    canvas.height = 1;

    return { blob, previewUrl, pageWidth, pageHeight, nativePdf: true };
  } finally {
    await pdf.destroy();
  }
}

private async processOcrImport(
  file: File,
  _aggressive: boolean
): Promise<void> {
  if (this.scanProcessing) return;

  this.scanProcessing = true;
  this.scanProcessingProgress = 10;
  this.scanProcessingStatus = 'Opening customer B/L...';

  try {
    await this.yieldToBrowser();

    this.scanProcessingProgress = 35;
    this.scanProcessingStatus = 'Preparing high-resolution background...';

    const prepared = await this.prepareManualBackground(file);

    this.releasePendingBackgroundObjectUrl();
    this.pendingBackgroundBlob = prepared.blob;
    this.pendingBackgroundObjectUrl = prepared.previewUrl;
    this.pendingBackgroundFileName = file.name;
    this.pendingBackgroundMimeType = prepared.blob.type || 'image/jpeg';

    // Keep full-quality bytes for Base64/Firestore, but render a lightweight
    // preview in the editor for smooth scrolling.
    await this.useEditorPreviewFromBlob(
      prepared.blob
    );

    this.scanProcessingProgress = 78;
    this.scanProcessingStatus = 'Preparing manual data-key canvas...';
    await this.yieldToBrowser();

    /*
     * Original static headings/text/logo are NOT reconstructed with OCR.
     * The backend will create the cleaned Original background on Save.
     * Until Save completes, use the source preview so Original is never blank.
     */
    // EMPTY canvas by design: the customer B/L itself is the complete visual
    // template. User-added ERP data keys are the only overlay fields.
    const nonNegotiable = this.createViewLayout({}, {
      pageWidthPx: prepared.pageWidth,
      pageHeightPx: prepared.pageHeight,
      globalOffsetX: 0,
      globalOffsetY: 0,
      backgroundImageUrl: prepared.previewUrl,
      backgroundColor: '#ffffff',
      textColor: '#111827',
      scanFileName: file.name,
    });

    const original = this.createViewLayout({}, {
      pageWidthPx: prepared.pageWidth,
      pageHeightPx: prepared.pageHeight,
      globalOffsetX: 0,
      globalOffsetY: 0,

      // Original never uses the uploaded/source background.
      backgroundImageUrl: '',

      backgroundColor: '#ffffff',
      textColor: '#111827',
      scanFileName: '',
    });

    /*
     * IMPORTANT:
     * A newly uploaded/replaced B/L is BACKGROUND-ONLY.
     * Do not carry default/legacy/template fields onto the uploaded image.
     * ERP data keys are added manually by the user after the upload.
     */
    nonNegotiable.fields = {};
    original.fields = {};

    this.ocrReviewGeneratedLayouts = { nonNegotiable, original };
    this.ocrReviewGeneratedFields = {};
    this.ocrReviewConfidence = {};
    this.ocrReviewSummary = {
      mapped: 0,
      staticTexts: 0,
      unmapped: 0,
      lowConfidence: 0,
      averageConfidence: 100,
      recognizedCharacters: 0,
    };
    this.ocrReviewSourceImage = prepared.previewUrl;
    this.ocrReviewSourceMode = prepared.nativePdf ? 'native-pdf' : 'ocr';
    this.ocrReviewVisible = true;

    this.scanProcessingProgress = 100;
    this.scanProcessingStatus = 'Ready — click Use This Template, then drag ERP data keys.';
  } catch (error: any) {
    console.error('B/L background preparation failed:', error);
    this.releasePendingBackgroundObjectUrl();
    this.pendingBackgroundBlob = null;
    this.pendingBackgroundFileName = '';
    this.pendingBackgroundMimeType = '';

    this.toastr.danger(
      error?.message || 'Customer B/L could not be prepared.',
      'Template Import Failed'
    );
  } finally {
    this.scanProcessing = false;
    setTimeout(() => {
      if (!this.scanProcessing) {
        this.scanProcessingProgress = 0;
        this.scanProcessingStatus = '';
      }
    }, 120);
  }
}

async regenerateOcrTemplate(): Promise<void> {
  if (!this.ocrReviewFile || this.scanProcessing) {
    return;
  }

  if (this.ocrReviewSourceMode === 'native-pdf') {
    this.toastr.info(
      'This PDF already contains native text and vector layout. Re-running OCR would not improve this reconstruction.',
      'Native PDF'
    );
    return;
  }

  this.ocrReviewVisible = false;
  this.ocrReviewAttempt += 1;

  /*
   * Retry intentionally uses a different OCR strategy:
   * - stronger preprocessing
   * - alternate full-page PSM pass
   * - empty-cell recovery
   * - wider cell re-OCR
   * The previous candidate is kept automatically if the retry is worse.
   */
  await this.processOcrImport(
    this.ocrReviewFile,
    true
  );
}

cancelOcrReview(): void {
  // Manual background import has no expensive designer snapshot to restore.

  this.resetPendingOcrReview(true);
  this.releasePendingBackgroundObjectUrl();
  this.pendingBackgroundBlob = null;
  this.pendingBackgroundFileName = '';
  this.pendingBackgroundMimeType = '';
}

async useOcrTemplate(): Promise<void> {
  const generatedLayouts =
    this.ocrReviewGeneratedLayouts;

  if (!generatedLayouts) {
    this.toastr.warning(
      'There is no generated OCR template to use.',
      'OCR Review'
    );
    return;
  }

  const selectedFileName =
    this.ocrReviewFileName;

  const selectedSourceTraceImage =
    String(this.ocrReviewSourceImage || '').trim();

  /*
   * Close/clear the review first, then yield one frame before rendering the
   * full designer. This prevents Angular from rendering the heavy review
   * candidate and the heavy designer field table at the same time.
   *
   * Ownership of generatedLayouts is transferred instead of JSON-cloning the
   * complete NON + Original layout again. applyViewLayout() still clones the
   * active field map so editing remains isolated from the stored view layout.
   */
  this.ocrReviewVisible = false;

  this.ocrReviewFile = null;
  this.ocrReviewSourceImage = '';
  this.ocrReviewGeneratedFields = {};
  this.ocrReviewGeneratedLayouts = null;
  this.ocrReviewConfidence = {};
  this.ocrReviewAttempt = 0;
  this.ocrReviewSourceMode = 'ocr';

  await this.yieldToBrowser();

  /*
   * BACKGROUND-FIRST MANUAL FLOW:
   * When a PDF/image is uploaded or replaced, the uploaded page itself is the
   * ONLY visual template. Old/default fields must not be rendered over it.
   *
   * Start with no ERP overlays. The user will drag/drop the required ERP
   * dataKeys manually onto the new background.
   */
  generatedLayouts.nonNegotiable.fields = {};

  // Original is always derived from the final NON data-key geometry.
  generatedLayouts.original =
    this.createOriginalLayoutFromNonNegotiable(
      generatedLayouts.nonNegotiable
    );

  this.viewLayouts =
    generatedLayouts;

  this.nonNegotiableScanFileName =
    selectedFileName;

  this.viewMode =
    'non-negotiable';

  this.applyViewLayout(
    'non-negotiable'
  );

  // Keep the source page only as a hidden tracing asset. It is never assigned
  // to backgroundImageUrl, so the final template is still made from separate
  // editable structure/text/data fields.
  // Source image is reference-only during import. Final template stores real editable text.
  this.form.sourceTraceImageUrl = '';


  await this.yieldToBrowser();

  const totalFields =
    Object.keys(
      this.fields || {}
    ).length;

  const structureFields =
    Object.entries(
      this.fields || {}
    ).filter(
      ([key, field]) =>
        this.isImportedStructureField(
          key,
          field
        )
    ).length;

  console.info(
    '[BL IMPORT] template activated',
    {
      totalFields,
      structureFields,
      editablePanelFields:
        totalFields -
        structureFields,
      mappedDataKeys:
        Object.values(
          this.fields || {}
        ).filter(
          field =>
            Boolean(
              String(
                field.dataKey ||
                ''
              ).trim()
            )
        ).length,
    }
  );

  this.toastr.success(
    'Customer B/L loaded as the locked visual template. Drag ERP data keys onto the exact value positions, then save.',
    'Template Ready'
  );

  window.scrollTo({
    top: 0,
    behavior: 'auto',
  });
}

private resetPendingOcrReview(
  clearBaseline: boolean
): void {
  this.ocrReviewVisible = false;
  this.ocrReviewSourceImage = '';
  this.ocrReviewGeneratedFields = {};
  this.ocrReviewGeneratedLayouts = null;
  this.ocrReviewConfidence = {};
  this.ocrReviewSourceMode = 'ocr';
  this.ocrReviewSummary = {
    mapped: 0,
    staticTexts: 0,
    unmapped: 0,
    lowConfidence: 0,
    averageConfidence: 0,
    recognizedCharacters: 0,
  };

  if (clearBaseline) {

    this.ocrReviewFile = null;
    this.ocrReviewFileName = '';
    this.ocrReviewAttempt = 0;
  }
}

private yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });
}

get ocrReviewFieldKeys(): string[] {
  return Object.keys(
    this.ocrReviewGeneratedFields ||
      {}
  );
}

getOcrReviewPageStyle(): Record<string, string> {
  const layout =
    this.ocrReviewGeneratedLayouts
      ?.nonNegotiable;

  return {
    width:
      `${this.toNumber(
        layout?.pageWidthPx,
        794
      )}px`,
    height:
      `${this.toNumber(
        layout?.pageHeightPx,
        1128
      )}px`,
    backgroundColor:
      layout?.backgroundColor ||
      '#ffffff',
    backgroundImage:
      layout?.backgroundImageUrl
        ? `url("${layout.backgroundImageUrl}")`
        : 'none',
    backgroundSize: '100% 100%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    color:
      layout?.textColor ||
      '#111827',
  };
}

getOcrReviewFieldStyle(
  key: string
): Record<string, string> {
  const field =
    this.ocrReviewGeneratedFields[
      key
    ];

  if (!field) {
    return {};
  }

  const review =
    this.ocrReviewConfidence[
      key
    ];

  const lowConfidence =
    Boolean(
      review &&
      (
        !review.mapped ||
        review.confidence <
          0.8
      )
    );

  const sourceTraceImageUrl =
    false
      ? String(this.ocrReviewSourceImage || '').trim()
      : '';

  const cropWidth = Math.max(
    1,
    this.toNumber(field.sourceCropWidth, field.width)
  );
  const cropHeight = Math.max(
    1,
    this.toNumber(field.sourceCropHeight, field.height)
  );
  const fieldWidth = Math.max(1, this.toNumber(field.width, 100));
  const fieldHeight = Math.max(1, this.toNumber(field.height, 30));
  const sourceScaleX = fieldWidth / cropWidth;
  const sourceScaleY = fieldHeight / cropHeight;
  const reviewPageWidth = this.toNumber(
    this.ocrReviewGeneratedLayouts?.nonNegotiable?.pageWidthPx,
    794
  );
  const reviewPageHeight = this.toNumber(
    this.ocrReviewGeneratedLayouts?.nonNegotiable?.pageHeightPx,
    1128
  );

  return {
    left:
      `${this.toNumber(
        field.x,
        0
      )}px`,
    top:
      `${this.toNumber(
        field.y,
        0
      )}px`,
    width:
      `${this.toNumber(
        field.width,
        100
      )}px`,
    height:
      `${this.toNumber(
        field.height,
        30
      )}px`,
    fontSize:
      `${this.toNumber(
        field.fontSize,
        9
      )}px`,
    lineHeight:
      String(
        this.toNumber(
          field.lineHeight,
          1.25
        )
      ),
    fontWeight:
      field.fontWeight ||
      'normal',
    fontStyle:
      field.fontStyle ||
      'normal',
    textDecoration:
      field.textDecoration ||
      'none',
    textAlign:
      field.textAlign ||
      'left',
    backgroundColor:
      sourceTraceImageUrl
        ? 'transparent'
        : field.backgroundColor ||
          'transparent',
    backgroundImage: sourceTraceImageUrl
      ? `url("${sourceTraceImageUrl}")`
      : 'none',
    backgroundSize: sourceTraceImageUrl
      ? `${reviewPageWidth * sourceScaleX}px ${reviewPageHeight * sourceScaleY}px`
      : 'auto',
    backgroundPosition: sourceTraceImageUrl
      ? `${-this.toNumber(field.sourceCropX, field.x) * sourceScaleX}px ${-this.toNumber(field.sourceCropY, field.y) * sourceScaleY}px`
      : 'center',
    backgroundRepeat: 'no-repeat',
    color:
      sourceTraceImageUrl
        ? 'transparent'
        : field.textColor ||
      this.ocrReviewGeneratedLayouts?.nonNegotiable?.textColor ||
      '#111827',
    borderTopWidth:
      `${this.getFieldBorderWidth(
        field,
        'top'
      )}px`,
    borderRightWidth:
      `${this.getFieldBorderWidth(
        field,
        'right'
      )}px`,
    borderBottomWidth:
      `${this.getFieldBorderWidth(
        field,
        'bottom'
      )}px`,
    borderLeftWidth:
      `${this.getFieldBorderWidth(
        field,
        'left'
      )}px`,
    borderStyle:
      'solid',
    borderColor:
      '#111827',
    boxSizing:
      'border-box',
    padding:
      `${this.toNumber(field.contentPadding, 1)}px`,
    overflow:
      field.type === 'text' ? 'visible' : 'hidden',
    outline:
      lowConfidence
        ? '2px dashed #f59e0b'
        : 'none',
    outlineOffset:
      lowConfidence
        ? '2px'
        : '0',
    zIndex:
      String(
        this.toNumber(
          field.zIndex,
          10
        )
      ),
  };
}

getOcrReviewFieldValue(
  key: string
): string {
  const field =
    this.ocrReviewGeneratedFields[
      key
    ];

  if (!field) {
    return '';
  }


  if (
    key === 'footer'
  ) {
    return this.fixedFooterText;
  }

  if (
    this.isDocumentTypeField(
      key,
      field
    )
  ) {
    return 'NON-NEGOTIABLE';
  }

  switch (
    field.type
  ) {
    case 'company-name':
      return (
        this.company.name ||
        'Company Name'
      );

    case 'company-address':
      return (
        this.company.address ||
        'Company Address'
      );

    case 'company-contact':
      return [
        this.company.telephone,
        this.company.email,
      ]
        .filter(Boolean)
        .join(' · ');

    case 'digital-stamp':
      return 'DIGITAL STAMP';

    case 'company-logo':
      return '';

    default:
      return (
        field.previewValue ||
        (
          field.dataKey
            ? `{{${field.dataKey}}}`
            : ''
        )
      );
  }
}

viewingTemplate: BlTemplate | null = null;

  constructor(
    private firestore: Firestore,
    private toastr: NbToastrService,
    private dialogService: NbDialogService
  ) {}

  private fieldKeysSource:
  Record<string, BlFieldConfig> |
  null = null;

private cachedFieldKeys:
  string[] = [];

private fieldPanelKeysSource:
  Record<string, BlFieldConfig> |
  null = null;

private cachedFieldPanelKeys:
  string[] = [];

private isImportedStructureField(
  key: string,
  field?: BlFieldConfig
): boolean {
  if (!field) {
    return false;
  }

  const normalizedKey =
    String(key || '')
      .toLowerCase();

  const displayName =
    String(
      field.displayName || ''
    )
      .toLowerCase()
      .trim();

  const emptyContent =
    !String(
      field.label || ''
    ).trim() &&
    !String(
      field.dataKey || ''
    ).trim() &&
    !String(
      field.previewValue || ''
    ).trim();

  return (
    emptyContent &&
    field.type === 'text' &&
    (
      normalizedKey.startsWith(
        'pdf_structure_'
      ) ||
      normalizedKey.includes(
        '_structure'
      ) ||
      normalizedKey.startsWith(
        'import_empty_structure'
      ) ||
      normalizedKey.startsWith(
        'import_retry_structure'
      ) ||
      normalizedKey.startsWith(
        'import_structure'
      ) ||
      displayName ===
        'imported bl structure' ||
      displayName ===
        'pdf structure'
    )
  );
}

get fieldKeys(): string[] {
  if (
    this.fieldKeysSource !==
    this.fields
  ) {
    this.fieldKeysSource =
      this.fields;

    this.cachedFieldKeys =
      Object.keys(
        this.fields
      );
  }

  return this.cachedFieldKeys;
}

/*
 * Structure-only OCR rectangles still render on the page, but they do not
 * belong in the heavy Field Positions form. Rendering hundreds of rows with
 * inputs/selects was the main reason "Use This Template" could freeze the UI.
 */
get fieldPanelKeys(): string[] {
  if (
    this.fieldPanelKeysSource !==
    this.fields
  ) {
    this.fieldPanelKeysSource =
      this.fields;

    this.cachedFieldPanelKeys =
      Object.keys(
        this.fields
      ).filter(
        key =>
          !this.isImportedStructureField(
            key,
            this.fields[key]
          )
      );
  }

  return this.cachedFieldPanelKeys;
}

  get currentPageWidth(): number {
    return this.toNumber(this.form.pageWidthPx, 794);
  }

  get currentPageHeight(): number {
    return this.toNumber(this.form.pageHeightPx, 1128);
  }

  async ngOnInit(): Promise<void> {
    try {
      this.companyId = await this.resolveCompanyId();

      if (!this.companyId) {
        this.toastr.danger(
          'Company ID could not be resolved.',
          'Company Missing'
        );
        return;
      }

      await this.loadCompany();
      await this.loadTemplates(); // UI/list only; editor data loads on demand.
    } catch (error) {
      console.error('BL template initialization error:', error);

      this.toastr.danger(
        'BL template module could not be loaded.',
        'Load Failed'
      );
    } finally {
      this.loading = false;
    }
  }

  ngOnDestroy(): void {
    this.ocrReviewFile = null;


    this.releasePendingBackgroundObjectUrl();
    this.releaseEditorBackgroundPreview();
    this.pendingBackgroundBlob = null;
    this.stopDataKeyAutoScroll();


    this.removeDragListeners();
    this.removeResizeListeners();
  }


  // ________ Download PDF _______________ //


  async downloadTemplatePdf(
  template: BlTemplate,
  event?: MouseEvent
): Promise<void> {
  event?.stopPropagation();
  event?.preventDefault();

  if (!template?.id || this.downloadingTemplateId) {
    return;
  }

  this.downloadingTemplateId = template.id;

  try {
    template = await this.getFullTemplate(template.id);
    const renderLayout =
      this.getTemplateLayoutForMode(
        template,
        this.viewMode
      );

    const pageWidth = this.toNumber(
      renderLayout.pageWidthPx,
      794
    );

    const pageHeight = this.toNumber(
      renderLayout.pageHeightPx,
      1128
    );

    const pdf = new jsPDF({
      orientation:
        pageWidth > pageHeight
          ? 'landscape'
          : 'portrait',
      unit: 'px',
      format: [pageWidth, pageHeight],
      hotfixes: ['px_scaling'],
      compress: true,
    });

    // Page background color
    const backgroundRgb = this.hexToRgb(
      renderLayout.backgroundColor || '#ffffff'
    );

    pdf.setFillColor(
      backgroundRgb[0],
      backgroundRgb[1],
      backgroundRgb[2]
    );

    pdf.rect(
      0,
      0,
      pageWidth,
      pageHeight,
      'F'
    );

    // Background image and company logo
    const [backgroundImage, companyLogo] =
      await Promise.all([
        this.loadPdfImage(
          renderLayout.backgroundImageUrl
        ),
        this.loadPdfImage(this.company.logo),
      ]);

    if (backgroundImage && !this.isOriginalView()) {
      pdf.addImage(
        backgroundImage,
        this.getPdfImageFormat(backgroundImage),
        0,
        0,
        pageWidth,
        pageHeight
      );
    }

    const offsetX = this.toNumber(
      renderLayout.globalOffsetX,
      0
    );

    const offsetY = this.toNumber(
      renderLayout.globalOffsetY,
      0
    );

    const fields = Object.entries(
      renderLayout.fields || {}
    )
      .filter(([, field]) => field?.visible)
      .sort(
        ([, first], [, second]) =>
          this.toNumber(first.zIndex, 0) -
          this.toNumber(second.zIndex, 0)
      );

    for (const [key, field] of fields) {
      const x =
        this.toNumber(field.x, 0) + offsetX;

      const y =
        this.toNumber(field.y, 0) + offsetY;

      const width = this.toNumber(
        field.width,
        100
      );

      const height = this.toNumber(
        field.height,
        30
      );

      const scanBackedNonNegotiable =
        !this.isOriginalView() &&
        Boolean(String(renderLayout.backgroundImageUrl || '').trim());

      const fieldBackground =
        String(field.backgroundColor || '').trim();

      if (
        !this.isOriginalView() &&
        !scanBackedNonNegotiable &&
        fieldBackground &&
        fieldBackground !== 'transparent'
      ) {
        const fillRgb =
          this.hexToRgb(fieldBackground);

        pdf.setFillColor(
          fillRgb[0],
          fillRgb[1],
          fillRgb[2]
        );

        pdf.rect(
          x,
          y,
          width,
          height,
          'F'
        );
      }

      if (!this.isOriginalView() && !scanBackedNonNegotiable) {
        this.drawPdfFieldBorders(
          pdf,
          field,
          x,
          y,
          width,
          height
        );
      }

      if (
        field.type === 'company-logo' &&
        companyLogo
      ) {
        this.addPdfImageContained(
          pdf,
          companyLogo,
          x,
          y,
          width,
          height
        );

        continue;
      }

      const fontSize = this.toNumber(
        field.fontSize,
        9
      );

      const textRgb = this.hexToRgb(
        field.textColor ||
        renderLayout.textColor ||
        '#111827'
      );

      pdf.setTextColor(
        textRgb[0],
        textRgb[1],
        textRgb[2]
      );

      const padding = 4;
      const usableWidth = Math.max(
        1,
        width - padding * 2
      );

      let textY = y + fontSize;
      const textX = this.getPdfTextX(
        field.textAlign,
        x,
        width,
        padding
      );

      const alignment =
        field.textAlign === 'center'
          ? 'center'
          : field.textAlign === 'right'
            ? 'right'
            : 'left';

      if (field.showLabel && field.label) {
        const labelSize = Math.max(
          5,
          fontSize * 0.72
        );

        pdf.setFont(
          this.getPdfFontFamily(field),
          'bold'
        );

        pdf.setFontSize(labelSize);

        pdf.text(
          String(field.label),
          textX,
          y + labelSize + 2,
          {
            align: alignment,
          }
        );

        textY =
          y +
          labelSize +
          fontSize +
          5;
      }

      const fontStyle =
        field.fontWeight === 'bold' &&
        field.fontStyle === 'italic'
          ? 'bolditalic'
          : field.fontWeight === 'bold'
            ? 'bold'
            : field.fontStyle === 'italic'
              ? 'italic'
              : 'normal';

      pdf.setFont(
        this.getPdfFontFamily(field),
        fontStyle
      );

      pdf.setFontSize(fontSize);

      const value = this.getPdfFieldValue(
        template,
        key,
        field
      );

      const lines = pdf.splitTextToSize(
        value,
        usableWidth
      );

      const lineHeight =
        fontSize *
        this.toNumber(
          field.lineHeight,
          1.25
        );

      const availableHeight = Math.max(
        fontSize,
        y + height - textY
      );

      const maximumLines = Math.max(
        1,
        Math.floor(
          availableHeight / lineHeight
        )
      );

      pdf.text(
        lines.slice(0, maximumLines),
        textX,
        textY,
        {
          align: alignment,
          lineHeightFactor: this.toNumber(
            field.lineHeight,
            1.25
          ),
        }
      );
    }

    const filename = String(
      template.templateName ||
        'BL-Template'
    )
      .trim()
      .replace(
        /[<>:"/\\|?*\u0000-\u001F]/g,
        '-'
      )
      .replace(/\s+/g, '-');

    pdf.save(`${filename}-${this.viewMode}.pdf`);

    this.toastr.success(
      'BL template PDF downloaded.',
      'Downloaded'
    );
  } catch (error: any) {
    console.error(
      'BL PDF download failed:',
      error
    );

    this.toastr.danger(
      error?.message ||
        'PDF download failed.',
      'Download Failed'
    );
  } finally {
    this.downloadingTemplateId = null;
  }
}

private getPdfFieldValue(
  template: BlTemplate,
  key: string,
  resolvedField?: BlFieldConfig
): string {
  const field =
    resolvedField ||
    this.getTemplateLayoutForMode(
      template,
      this.viewMode
    ).fields?.[key];

  if (!field) {
    return '';
  }

  if (key === 'footer') {
    return this.fixedFooterText;
  }

  if (this.isDocumentTypeField(key, field)) {
    return this.getDocumentTypeText();
  }

  switch (field.type) {
    case 'company-name':
      return this.company.name || '';

    case 'company-address':
      return this.company.address || '';

    case 'company-contact':
      return [
        this.company.telephone
          ? `Tel: ${this.company.telephone}`
          : '',
        this.company.email
          ? `Email: ${this.company.email}`
          : '',
        this.company.ntn
          ? `NTN: ${this.company.ntn}`
          : '',
      ]
        .filter(Boolean)
        .join(' | ');

    case 'text':
      return String(
        field.previewValue ||
          field.label ||
          ''
      );

    case 'company-logo':
      return '';

    case 'container-table':
      return [
        'CONT0012345 | SEAL001 | 1200 KG | 25 | 18.50',
        'CONT0067890 | SEAL002 | 1350 KG | 30 | 20.10',
      ].join('\n');

    case 'field':
    default:
      if (field.dataKey === 'company.phone') return this.company.telephone || '';
      if (field.dataKey === 'company.email') return this.company.email || '';
      if (field.dataKey === 'company.ntn') return this.company.ntn || '';

      return (
        String(field.previewValue || '').trim() ||
        (field.dataKey
          ? `{{${field.dataKey}}}`
          : '')
      );
  }
}

private getPdfTextX(
  alignment: BlTextAlign,
  x: number,
  width: number,
  padding: number
): number {
  if (alignment === 'center') {
    return x + width / 2;
  }

  if (alignment === 'right') {
    return x + width - padding;
  }

  return x + padding;
}

private drawPdfFieldBorders(
  pdf: jsPDF,
  field: BlFieldConfig,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  pdf.setDrawColor(17, 24, 39);

  const drawLine = (
    size: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ) => {
    if (size <= 0) return;

    pdf.setLineWidth(size);
    pdf.line(x1, y1, x2, y2);
  };

  drawLine(
    this.getFieldBorderWidth(field, 'top'),
    x,
    y,
    x + width,
    y
  );

  drawLine(
    this.getFieldBorderWidth(field, 'right'),
    x + width,
    y,
    x + width,
    y + height
  );

  drawLine(
    this.getFieldBorderWidth(field, 'bottom'),
    x,
    y + height,
    x + width,
    y + height
  );

  drawLine(
    this.getFieldBorderWidth(field, 'left'),
    x,
    y,
    x,
    y + height
  );
}

private async loadPdfImage(
  imageUrl: string
): Promise<string | null> {
  const url = String(imageUrl || '').trim();

  if (!url) {
    return null;
  }

  if (url.startsWith('data:image/')) {
    return url;
  }

  try {
    const response = await fetch(url);

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();

    return await new Promise<string | null>(
      (resolve) => {
        const reader = new FileReader();

        reader.onload = () =>
          resolve(
            typeof reader.result === 'string'
              ? reader.result
              : null
          );

        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      }
    );
  } catch (error) {
    console.warn(
      'PDF image could not be loaded:',
      error
    );

    return null;
  }
}

private getPdfImageFormat(
  dataUrl: string
): 'PNG' | 'JPEG' | 'WEBP' {
  if (dataUrl.startsWith('data:image/png')) {
    return 'PNG';
  }

  if (dataUrl.startsWith('data:image/webp')) {
    return 'WEBP';
  }

  return 'JPEG';
}

private addPdfImageContained(
  pdf: jsPDF,
  imageData: string,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  const properties =
    pdf.getImageProperties(imageData);

  const imageRatio =
    properties.width / properties.height;

  const boxRatio = width / height;

  let renderedWidth = width;
  let renderedHeight = height;

  if (imageRatio > boxRatio) {
    renderedHeight = width / imageRatio;
  } else {
    renderedWidth = height * imageRatio;
  }

  pdf.addImage(
    imageData,
    this.getPdfImageFormat(imageData),
    x + (width - renderedWidth) / 2,
    y + (height - renderedHeight) / 2,
    renderedWidth,
    renderedHeight
  );
}

private hexToRgb(
  hexColor: string
): [number, number, number] {
  let value = String(hexColor || '')
    .replace('#', '')
    .trim();

  if (value.length === 3) {
    value = value
      .split('')
      .map((character) => character + character)
      .join('');
  }

  if (!/^[0-9a-fA-F]{6}$/.test(value)) {
    return [17, 24, 39];
  }

  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}


  private getEmptyForm(): Partial<BlTemplate> {
    return {
      templateName: 'BL Template 1',

      pageWidthPx: 794,
      pageHeightPx: 1128,

      globalOffsetX: 0,
      globalOffsetY: 0,

      backgroundImageUrl: '',
      sourceTraceImageUrl: '',
      backgroundColor: '#ffffff',
      textColor: '#111827',

      isDefault: true,
      isActive: true,
    };
  }

  private readLocalCompanyCandidate(): any {
    const keys = [
      'companyDetails',
      'companyData',
      'selectedCompany',
      'company',
      'companyInfo',
      'userData',
    ];

    const parse = (value: string | null): any => {
      if (!value) return null;
      try { return JSON.parse(value); } catch { return null; }
    };

    for (const key of keys) {
      const raw = parse(localStorage.getItem(key));
      if (!raw || typeof raw !== 'object') continue;

      const source = raw.company || raw.companyData || raw.companyDetails || raw.selectedCompany || raw.currentCompany || raw;
      if (!source || typeof source !== 'object') continue;

      const hasUsefulValue = [
        source.name, source.companyName, source.address, source.companyAddress,
        source.phone, source.telePhone, source.telephone, source.email, source.companyEmail,
        source.ntn, source.NTN, source.logo, source.logoBase64, source.companyLogo,
      ].some(value => value !== undefined && value !== null && String(value).trim() !== '');

      if (hasUsefulValue) return source;
    }

    return {};
  }

  async loadCompany(): Promise<void> {
    if (!this.companyId) {
      throw new Error('Company ID is missing before loading company data.');
    }

    const local = this.readLocalCompanyCandidate();
    let remote: any = {};

    // Only fetch the company profile when locally cached display fields are
    // missing. Company ID resolution/validation happens in resolveCompanyId().
    const directLogoCandidate = [
      localStorage.getItem('companyLogoBase64'),
      localStorage.getItem('companyLogo'),
      localStorage.getItem('logoBase64'),
      localStorage.getItem('logo'),
      local.logo, local.logoBase64, local.companyLogo,
      local.companyLogoBase64, local.logoUrl,
    ];
    const present = (...values: any[]): boolean =>
      values.some(value => value !== undefined && value !== null && String(value).trim() !== '');

    const localCompanyId = String(local.id || local.companyId || local.companyKey || '').trim();
    const profileMatchesResolvedCompany = !localCompanyId || localCompanyId === this.companyId;
    const localProfileComplete = profileMatchesResolvedCompany &&
      present(local.name, local.companyName, local.legalName) &&
      present(local.address, local.companyAddress, local.officeAddress) &&
      present(local.email, local.companyEmail, local.contactEmail) &&
      present(local.telePhone, local.telephone, local.phone, local.phoneNumber, local.contactNumber) &&
      present(local.ntn, local.NTN, local.ntnNumber, local.taxNumber) &&
      present(...directLogoCandidate);

    if (!localProfileComplete) {
      try {
        const companyReference = doc(this.firestore, 'companies', this.companyId);
        const companySnapshot = await getDoc(companyReference);
        if (companySnapshot.exists()) remote = companySnapshot.data() || {};
      } catch (error) {
        console.warn('Company Firestore fallback could not be loaded:', error);
      }
    }

    // Local/session company profile wins; Firestore only fills missing values.
    const first = (...values: any[]) => {
      const value = values.find(item => item !== undefined && item !== null && String(item).trim() !== '');
      return value === undefined ? '' : String(value).trim();
    };

    const directLogo = first(
      localStorage.getItem('companyLogoBase64'),
      localStorage.getItem('companyLogo'),
      localStorage.getItem('logoBase64'),
      localStorage.getItem('logo')
    );

    this.company = {
      id: this.companyId,
      name: first(local.name, local.companyName, local.legalName, remote.name, remote.companyName),
      address: first(local.address, local.companyAddress, local.officeAddress, remote.address, remote.companyAddress),
      email: first(local.email, local.companyEmail, local.contactEmail, remote.email, remote.companyEmail),
      telephone: first(local.telePhone, local.telephone, local.phone, local.phoneNumber, local.contactNumber, remote.telePhone, remote.telephone, remote.phone, remote.contactNumber),
      ntn: first(local.ntn, local.NTN, local.ntnNumber, local.taxNumber, remote.ntn, remote.NTN),
      logo: first(directLogo, local.logo, local.logoBase64, local.companyLogo, local.companyLogoBase64, local.logoUrl, remote.logo, remote.companyLogo),
    };
  }

  // Lightweight list. For databases deployed before the metadata index existed,
  // recover legacy templates once instead of incorrectly displaying an empty list.
  async loadTemplates(templateToOpenId?: string): Promise<void> {
    try {
      const indexSnapshot = await getDocs(query(
        collection(this.firestore, this.templateIndexCollectionName),
        where('companyId', '==', this.companyId)
      ));

      // Normal path: index documents have no full-size backgrounds or field maps.
      this.templates = indexSnapshot.docs.map(item =>
        this.templateSummaryFromData(item.id, item.data())
      );

      if (!this.templates.length) {
        // Legacy recovery only; Firestore Web SDK cannot project fields from
        // existing documents. This one-time read may be large.
        const legacySnapshot = await getDocs(query(
          collection(this.firestore, this.collectionName),
          where('companyId', '==', this.companyId)
        ));
        this.templates = legacySnapshot.docs.map(item =>
          this.templateSummaryFromData(item.id, item.data())
        );
        if (this.templates.length) {
          // The original documents remain untouched. Index writes are best-effort:
          // a missing index write permission must not hide existing templates.
          void Promise.allSettled(legacySnapshot.docs.map(item => {
            const data = item.data();
            return setDoc(
              doc(this.firestore, this.templateIndexCollectionName, item.id),
              {
                companyId: String(data['companyId'] || this.companyId),
                templateName: String(data['templateName'] || 'BL Template'),
                isDefault: data['isDefault'] === true,
                isActive: data['isActive'] !== false,
                pageWidthPx: this.toNumber(data['pageWidthPx'], 794),
                pageHeightPx: this.toNumber(data['pageHeightPx'], 1128),
                defaultViewMode: data['defaultViewMode'] === 'original' ? 'original' : 'non-negotiable',
                ...(data['createdAt'] ? { createdAt: data['createdAt'] } : {}),
                ...(data['updatedAt'] ? { updatedAt: data['updatedAt'] } : {}),
              }
            );
          })).then(writes => {
            const failures = writes.filter(w => w.status === 'rejected');
            if (failures.length) {
              console.warn('[BL] Legacy templates shown but metadata index writes failed. Run the admin backfill and review Firestore rules.', failures[0]);
            }
          });
        }
      }

      this.templates.sort((a, b) => a.templateName.localeCompare(
        b.templateName, undefined, { numeric: true }
      ));
      if (templateToOpenId) {
        const toOpen = this.templates.find(t => t.id === templateToOpenId);
        if (toOpen) await this.editTemplate(toOpen, false);
      } else if (!this.selectedTemplate) {
        this.newTemplate(false);
      }
    } catch (error: any) {
      console.error('BL template list load error:', error);
      this.toastr.danger(
        error?.message || 'Template list could not be loaded. Check Firestore rules and company ID.',
        'Load Failed'
      );
    }
  }

  private templateSummaryFromData(id: string, value: any): BlTemplate {
    return {
      id,
      companyId: String(value['companyId'] || this.companyId),
      templateName: String(value['templateName'] || 'BL Template'),
      isDefault: value['isDefault'] === true,
      isActive: value['isActive'] !== false,
      pageWidthPx: this.toNumber(value['pageWidthPx'], 794),
      pageHeightPx: this.toNumber(value['pageHeightPx'], 1128),
      globalOffsetX: 0,
      globalOffsetY: 0,
      backgroundImageUrl: '',
      backgroundColor: '#ffffff',
      textColor: '#111827',
      fields: {},
      defaultViewMode: value['defaultViewMode'] === 'original' ? 'original' : 'non-negotiable',
      createdAt: value['createdAt'],
      updatedAt: value['updatedAt'],
    } as BlTemplate;
  }

  private async getFullTemplate(id: string): Promise<BlTemplate> {
    const reference = doc(this.firestore, this.collectionName, id);
    const snapshot = await getDoc(reference);
    if (!snapshot.exists()) throw new Error('Selected BL template no longer exists.');
    const raw = snapshot.data();
    if (String(raw['companyId'] || '') !== this.companyId) {
      throw new Error('Selected template does not belong to the active company.');
    }
    return this.normalizeTemplate(snapshot.id, raw);
  }

  private makeTemplateIndexPayload(template: BlTemplate): Record<string, any> {
    return {
      companyId: template.companyId,
      templateName: template.templateName,
      isDefault: template.isDefault,
      isActive: template.isActive,
      pageWidthPx: template.pageWidthPx,
      pageHeightPx: template.pageHeightPx,
      defaultViewMode: template.defaultViewMode || 'non-negotiable',
      createdAt: template.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
  }

  newTemplate(scroll = true): void {
    this.releaseEditorBackgroundPreview();

    this.selectedTemplate = null;
    this.activeFieldKey = null;

    this.form = {
      ...this.getEmptyForm(),
      templateName: `BL Template ${this.getNextTemplateNumber()}`,
      isDefault: this.templates.length === 0,
    };

    /*
     * New templates start clean. ERP dataKeys are placed manually from the
     * palette instead of pre-rendering the legacy/default template.
     */
    const baseFields: Record<string, BlFieldConfig> = {};

    this.viewMode = 'non-negotiable';

    const nonNegotiableLayout = this.createViewLayout(
      this.clone(baseFields)
    );

    nonNegotiableLayout.fields = {};

    this.viewLayouts = {
      nonNegotiable: nonNegotiableLayout,
      original: this.createOriginalLayoutFromNonNegotiable(
        nonNegotiableLayout
      ),
    };

    this.applyViewLayout('non-negotiable');

    if (scroll) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  async editTemplate(
  template: BlTemplate,
  scroll = true
): Promise<void> {
  if (!template?.id) return;
  const request = ++this.templateOpenRequest;
  this.openingTemplateId = template.id;
  try {
    template = await this.getFullTemplate(template.id);
    if (request !== this.templateOpenRequest) return;
  } catch (error: any) {
    if (request === this.templateOpenRequest) {
      console.error('BL template edit load error:', error);
      this.toastr.danger(error?.message || 'Template could not be opened.', 'Load Failed');
    }
    return;
  } finally {
    if (request === this.templateOpenRequest) this.openingTemplateId = null;
  }

  localStorage.setItem(
    'selected_bl_template_id',
    template.id
  );

  this.selectedTemplate = template;
  this.activeFieldKey = null;

  const fallbackPage: Partial<BlViewLayout> = {
    pageWidthPx: template.pageWidthPx,
    pageHeightPx: template.pageHeightPx,
    globalOffsetX: template.globalOffsetX,
    globalOffsetY: template.globalOffsetY,
    backgroundImageUrl: template.backgroundImageUrl,
    backgroundColor: template.backgroundColor,
    textColor: template.textColor || '#111827',
  };

  const legacyFields = this.normalizeFields(
    template.fields &&
    Object.keys(template.fields).length
      ? template.fields
      : this.getDefaultFields()
  );

  // Backward compatibility:
  // old templates have only `fields`; clone that starting point into BOTH views.
  // Once saved, the two layouts remain fully independent.
  this.viewLayouts = {
    nonNegotiable: this.normalizeViewLayout(
      template.viewLayouts?.nonNegotiable,
      legacyFields,
      fallbackPage
    ),
    original: template.viewLayouts?.original
      ? this.normalizeViewLayout(
          template.viewLayouts.original,
          legacyFields,
          fallbackPage
        )
      : this.createOriginalLayoutFromNonNegotiable(
          this.normalizeViewLayout(
            template.viewLayouts?.nonNegotiable,
            legacyFields,
            fallbackPage
          )
        ),
  };

  this.form = {
    templateName: template.templateName,
    isDefault: template.isDefault,
    isActive: template.isActive,
    sourceTraceImageUrl: String(template.sourceTraceImageUrl || '').trim(),
  };

  this.viewMode =
    template.defaultViewMode === 'original'
      ? 'original'
      : 'non-negotiable';

  this.applyViewLayout(this.viewMode);

  // Firestore/PDF keeps the original Base64. The live editor gets a small
  // temporary preview to avoid expensive high-resolution repaint on scroll.
  void this.refreshEditorPreviewFromSavedBackground();

  if (scroll) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
}

  async saveTemplate(): Promise<void> {
  const templateName = String(
    this.form.templateName || ''
  ).trim();

  if (!templateName) {
    this.toastr.warning(
      'Template name is required.',
      'Required'
    );
    return;
  }

  if (!this.companyId) {
    this.toastr.danger(
      'Company ID is missing.',
      'Save Failed'
    );
    return;
  }

  const pageWidthPx = this.toNumber(
    this.form.pageWidthPx,
    794
  );

  const pageHeightPx = this.toNumber(
    this.form.pageHeightPx,
    1128
  );

  if (
    pageWidthPx < 300 ||
    pageHeightPx < 300
  ) {
    this.toastr.warning(
      'Page dimensions are too small.',
      'Invalid Page Size'
    );
    return;
  }

  this.saving = true;

  try {
    const existingTemplateId =
      this.selectedTemplate?.id;

    const existing =
      Boolean(existingTemplateId);

    const templateId =
      existingTemplateId ||
      this.generateTemplateId();

    if (this.form.isDefault) {
      await this.removeExistingDefault(
        templateId
      );
    }

    // Capture the currently open view before saving.
    this.commitCurrentViewLayout();

    /*
     * BASE64 / FIRESTORE TEST MODE
     * ----------------------------
     * No background API/backend upload is used here.
     *
     * The browser converts the selected Blob directly into a Base64 data URL
     * and that string is persisted inside:
     *
     *   viewLayouts.nonNegotiable.backgroundImageUrl
     *
     * WARNING:
     * Firestore has a ~1 MiB single-document limit, so large/high-resolution
     * B/L images can make setDoc() fail. This is intentional for this test.
     */
    if (this.pendingBackgroundBlob && this.viewLayouts) {
      this.scanProcessingStatus =
        'Converting B/L background to Base64...';

      const base64Background =
        await this.blobToDataUrl(
          this.pendingBackgroundBlob
        );

      console.info(
        '[BL BASE64 TEST] background converted',
        {
          characters: base64Background.length,
          approximateBytes:
            new Blob([base64Background]).size,
          mimeType:
            this.pendingBackgroundMimeType,
          fileName:
            this.pendingBackgroundFileName,
        }
      );

      // NON uses the uploaded image directly from Firestore as a data URL.
      this.viewLayouts.nonNegotiable.backgroundImageUrl =
        base64Background;

      // Original remains white/data-keys-only.
      this.viewLayouts.original =
        this.createOriginalLayoutFromNonNegotiable(
          this.viewLayouts.nonNegotiable
        );

      this.viewLayouts.original.backgroundImageUrl = '';
      this.viewLayouts.original.scanFileName = '';

      /*
       * Keep the canonical Base64 only in viewLayouts for Firestore/PDF.
       * The visible editor continues using editorBackgroundPreviewUrl.
       * Avoid pushing the huge Base64 string back through live Angular styles.
       */
      this.form.backgroundImageUrl =
        this.viewMode === 'original'
          ? ''
          : base64Background;

      this.releasePendingBackgroundObjectUrl();
      this.pendingBackgroundBlob = null;
      this.pendingBackgroundFileName = '';
      this.pendingBackgroundMimeType = '';
      this.scanProcessingStatus = '';
    }

    /*
     * Keep Original deterministic on EVERY save, even when no background
     * upload is pending. Any NON move/resize/dataKey change is mirrored exactly.
     */
    if (this.viewLayouts) {
      this.viewLayouts.original =
        this.createOriginalLayoutFromNonNegotiable(
          this.viewLayouts.nonNegotiable
        );

      if (this.viewMode === 'original') {
        this.applyViewLayout('original');
      }
    }

    const nonNegotiableSourceFile = String(
      this.viewLayouts?.nonNegotiable?.scanFileName ||
      this.nonNegotiableScanFileName ||
      ''
    ).trim();

    /*
     * Exact-source templates keep the imported page as the NON background.
     * scanFileName confirms that a source document was imported.
     */
    /*
     * Source upload is mandatory only when creating a brand-new template.
     *
     * Existing/legacy templates may pre-date the BL import pipeline and
     * therefore legitimately have no scanFileName. They must still remain
     * fully editable and saveable.
     */
    // A source PDF/image is optional. Users may either build an editable ERP
    // template from the existing designer or use a locked PDF/image background
    // and place ERP data keys manually.

    if (!this.viewLayouts) {
      const baseFields = this.prepareFieldsForSave();
      this.viewLayouts = {
        nonNegotiable: this.createViewLayout(this.clone(baseFields)),
        original: this.createViewLayout(this.clone(baseFields)),
      };
    }

    const normalizedNonNegotiableLayout =
      this.normalizeViewLayout(
        this.viewLayouts.nonNegotiable,
        this.viewLayouts.nonNegotiable.fields,
        this.viewLayouts.nonNegotiable
      );

    const normalizedOriginalLayout =
      this.normalizeViewLayout(
        this.viewLayouts.original,
        this.viewLayouts.original.fields,
        this.viewLayouts.original
      );

    const normalizedViewLayouts: BlTemplateViewLayouts = {
      nonNegotiable: normalizedNonNegotiableLayout,
      original: normalizedOriginalLayout,
    };

    // Keep legacy `fields` synced to Non-Negotiable so older readers keep working.
    const normalizedFields =
      this.clone(normalizedNonNegotiableLayout.fields);

    const payload: BlTemplate = {
      id: templateId,
      companyId: this.companyId,
      templateName,

      pageWidthPx,
      pageHeightPx,

      globalOffsetX: this.toNumber(
        this.form.globalOffsetX,
        0
      ),

      globalOffsetY: this.toNumber(
        this.form.globalOffsetY,
        0
      ),

      // Canonical background lives in viewLayouts.nonNegotiable. Do not duplicate
      // a potentially large JPEG data URL at the top level of the Firestore doc.
      backgroundImageUrl: '',

      sourceTraceImageUrl: '',

      backgroundColor: String(
        this.form.backgroundColor ||
        '#ffffff'
      ),

      textColor: String(
        this.form.textColor ||
        '#111827'
      ),

      isDefault:
        Boolean(this.form.isDefault),

      isActive:
        this.form.isActive !== false,

      fields: normalizedFields,
      viewLayouts: normalizedViewLayouts,
      views: this.templateViews,
      defaultViewMode: this.viewMode,

      createdAt:
  existing
    ? (
        this.selectedTemplate?.createdAt ||
        serverTimestamp()
      )
    : serverTimestamp(),

updatedAt: serverTimestamp(),

    };

    const templateReference = doc(
      this.firestore,
      this.collectionName,
      templateId
    );

    console.log(
      'Saving BL template to:',
      `${this.collectionName}/${templateId}`
    );

    console.log(
      'Custom fields being saved:',
      Object.entries(normalizedFields)
        .filter(([key]) =>
          key.startsWith('custom_')
        )
        .map(([key, field]) => ({
          key,
          displayName:
            field.displayName,
          heading: field.label,
          dataKey: field.dataKey,
          previewValue:
            field.previewValue,
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height,
        }))
    );

    await setDoc(
      templateReference,
      payload
    );

    const savedSnapshot =
      await getDoc(templateReference);

    if (!savedSnapshot.exists()) {
      throw new Error(
        'Template document does not exist after save.'
      );
    }

    const savedData =
      savedSnapshot.data() as BlTemplate;

    const verifyLayoutSaved = (
      mode: 'original' | 'nonNegotiable',
      expected: Record<string, BlFieldConfig>,
      actual: Record<string, BlFieldConfig> | undefined
    ): void => {
      const expectedKeys = Object.keys(expected || {});
      const missingKeys = expectedKeys.filter(
        key => !actual?.[key]
      );

      if (missingKeys.length) {
        throw new Error(
          `Firestore did not return ${mode} fields: ${missingKeys.join(', ')}`
        );
      }
    };

    verifyLayoutSaved(
      'nonNegotiable',
      normalizedViewLayouts.nonNegotiable.fields,
      savedData.viewLayouts?.nonNegotiable?.fields
    );

    verifyLayoutSaved(
      'original',
      normalizedViewLayouts.original.fields,
      savedData.viewLayouts?.original?.fields
    );

    // Write metadata after the full template is confirmed saved.
    await setDoc(
      doc(this.firestore, this.templateIndexCollectionName, templateId),
      this.makeTemplateIndexPayload(payload)
    );

    localStorage.setItem(
      'selected_bl_template_id',
      templateId
    );

    this.toastr.success(
      existing
        ? 'BL template updated successfully.'
        : 'BL template created successfully.',
      existing
        ? 'Updated'
        : 'Created'
    );

    await this.loadTemplates(templateId);
  } catch (error: any) {
    console.error(
      'BL template save error:',
      error
    );

    this.toastr.danger(
      error?.message ||
        'BL template save failed.',
      'Save Failed'
    );
  } finally {
    this.saving = false;
  }
}

  async deleteTemplate(
    template: BlTemplate
  ): Promise<void> {
    if (!template?.id) {
      return;
    }

    const confirmed = window.confirm(
      `Delete "${template.templateName}" permanently?`
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteDoc(
        doc(
          this.firestore,
          this.collectionName,
          template.id
        )
      );

      await deleteDoc(doc(
        this.firestore, this.templateIndexCollectionName, template.id
      ));
      this.templateOpenRequest++;

      this.toastr.success(
        'BL template deleted successfully.',
        'Deleted'
      );

      if (this.selectedTemplate?.id === template.id) {
        this.newTemplate(false);
      }

      await this.loadTemplates();
    } catch (error: any) {
      console.error('BL template delete error:', error);

      this.toastr.danger(
        error?.message || 'Template delete failed.',
        'Delete Failed'
      );
    }
  }

  resetLayout(): void {
    const confirmed = window.confirm(
      'Reset all BL fields to the default layout?'
    );

    if (!confirmed) {
      return;
    }

    const resetLayout =
      this.createViewLayout(
        this.getDefaultFields()
      );

    if (!this.viewLayouts) {
      this.viewLayouts = {
        nonNegotiable: this.createViewLayout(this.getDefaultFields()),
        original: this.createViewLayout(this.getDefaultFields()),
      };
    }

    if (this.viewMode === 'original') {
      this.viewLayouts.original = resetLayout;
    } else {
      this.viewLayouts.nonNegotiable = resetLayout;
    }

    this.applyViewLayout(this.viewMode);
    this.activeFieldKey = null;
  }

  onPageSizeChange(): void {
    this.form.pageWidthPx = this.clamp(
      this.form.pageWidthPx,
      500,
      2000,
      794
    );

    this.form.pageHeightPx = this.clamp(
      this.form.pageHeightPx,
      700,
      3000,
      1128
    );

    this.keepAllFieldsInsidePage();
  }

  selectField(key: string): void {
    if (this.fields[key]) {
      this.activeFieldKey = key;
    }
  }

  toggleAddFieldForm(): void {
  this.showAddFieldForm = !this.showAddFieldForm;

  if (!this.showAddFieldForm) {
    this.resetNewFieldForm();
  }
}

addCustomField(): void {
  const fieldType: 'field' | 'text' =
    this.newField.type === 'text'
      ? 'text'
      : 'field';

  const fieldName = String(
    this.newField.name || ''
  ).trim();

  const heading = String(
    this.newField.heading || ''
  ).trim();

  const previewValue = String(
    this.newField.previewValue || ''
  ).trim();

  const dataKey = String(
    this.newField.dataKey || ''
  ).trim();

  if (!fieldName) {
    this.toastr.warning(
      'Field name is required.',
      'Required'
    );

    return;
  }

  if (
    fieldType === 'field' &&
    !dataKey
  ) {
    this.toastr.warning(
      'Data key is required for a dynamic BL field.',
      'Required'
    );

    return;
  }

  if (
    fieldType === 'text' &&
    !previewValue
  ) {
    this.toastr.warning(
      'Static text value is required.',
      'Required'
    );

    return;
  }

  const key =
    this.createUniqueCustomFieldKey(
      fieldName
    );

  const customFieldCount =
    Object.keys(this.fields).filter(
      (fieldKey) =>
        this.isCustomField(fieldKey)
    ).length;

  const fieldWidth = Math.min(
    fieldType === 'text'
      ? 300
      : 220,
    this.currentPageWidth
  );

  const fieldHeight =
    fieldType === 'text'
      ? 30
      : 55;

  const proposedX =
    30 +
    (
      customFieldCount % 4
    ) *
      25;

  const proposedY =
    30 +
    (
      customFieldCount % 10
    ) *
      65;

  const field: BlFieldConfig = {
    key,

    displayName: fieldName,
    type: fieldType,

    label:
      heading ||
      fieldName,

    dataKey:
      fieldType === 'field'
        ? dataKey
        : '',

    previewValue,

    x: proposedX,
    y: proposedY,

    width: fieldWidth,
    height: fieldHeight,

    fontSize: 9,
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none',
    textAlign: 'left',
    lineHeight: 1.25,

    showLabel:
      fieldType === 'field'
        ? Boolean(
            this.newField.showLabel
          )
        : false,

    visible: true,
    locked: false,

    borderWidth:
  fieldType === 'field'
    ? 1
    : 0,

borderTopWidth:
  fieldType === 'field'
    ? 1
    : 0,

borderRightWidth:
  fieldType === 'field'
    ? 1
    : 0,

borderBottomWidth:
  fieldType === 'field'
    ? 1
    : 0,

borderLeftWidth:
  fieldType === 'field'
    ? 1
    : 0,

    zIndex:
      Object.keys(
        this.fields
      ).length + 20,
  };

  this.normalizeEditableField(
    field
  );

  this.fields = {
    ...this.fields,
    [key]: field,
  };

  this.activeFieldKey = key;
  this.showAddFieldForm = false;

  this.resetNewFieldForm();

  this.toastr.success(
    `"${fieldName}" added to the template.`,
    fieldType === 'text'
      ? 'Static Text Added'
      : 'Dynamic Field Added'
  );
}



canDeleteField(key: string): boolean {
  if (!this.fields[key]) {
    return false;
  }

  // Footer is compulsory.
  return key !== 'footer';
}

removeField(key: string): void {
  const field = this.fields[key];

  if (!field) {
    return;
  }

  if (!this.canDeleteField(key)) {
    this.toastr.warning(
      'The Portus footer is required and cannot be removed.',
      'Protected Field'
    );

    return;
  }

  const fieldLabel =
    this.getFieldDisplayName(key) ||
    field.label ||
    'Field';

  const confirmed = window.confirm(
    `Remove "${fieldLabel}" from this template?`
  );

  if (!confirmed) {
    return;
  }

  const updatedFields = {
    ...this.fields,
  };

  delete updatedFields[key];

  this.fields = updatedFields;

  if (this.activeFieldKey === key) {
    this.activeFieldKey = null;
  }

  this.toastr.success(
    `"${fieldLabel}" removed. Save the template to persist this change.`,
    'Field Removed'
  );
}

isCustomField(key: string): boolean {
  return key.startsWith('custom_');
}

private createUniqueCustomFieldKey(
  label: string
): string {
  const slug =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') ||
    'field';

  let key = `custom_${slug}`;
  let counter = 2;

  while (this.fields[key]) {
    key = `custom_${slug}_${counter}`;
    counter += 1;
  }

  return key;
}

private resetNewFieldForm(): void {
  this.newField = {
    type: 'field',

    name: '',
    heading: '',
    previewValue: '',
    dataKey: '',

    showLabel: true,
  };
}



  private getCssFontFamily(
    field?: BlFieldConfig
  ): string {
    switch (field?.fontFamily) {
      case 'times':
        return '"Times New Roman", Times, serif';

      case 'courier':
        return '"Courier New", Courier, monospace';

      case 'helvetica':
      default:
        // Keeps existing/legacy templates visually unchanged.
        return 'Arial, Helvetica, sans-serif';
    }
  }

  private getPdfFontFamily(
    field?: BlFieldConfig
  ): 'helvetica' | 'times' | 'courier' {
    return field?.fontFamily === 'times'
      ? 'times'
      : field?.fontFamily === 'courier'
        ? 'courier'
        : 'helvetica';
  }

  get canUndoAutoAlign(): boolean {
    return Boolean(this.autoAlignUndoSnapshot);
  }

  /**
   * PRINT ALIGNMENT:
   * Straightens manually placed ERP data keys without changing their content,
   * font size, width, height or old template geometry on load.
   *
   * Only fields whose left/top edges are already close to one another are
   * snapped together, so separate B/L columns stay separate.
   */
  autoAlignDataKeys(): void {
    const candidates = Object.entries(
      this.fields || {}
    ).filter(
      ([key, field]) =>
        key !== 'footer' &&
        Boolean(String(field?.dataKey || '').trim()) &&
        field?.visible !== false &&
        field?.type !== 'company-logo' &&
        field?.type !== 'digital-stamp' &&
        field?.type !== 'container-table'
    );

    if (candidates.length < 2) {
      this.toastr.info(
        'Place at least two ERP data keys before using Auto Align.',
        'Auto Align'
      );
      return;
    }

    this.autoAlignUndoSnapshot = {};
    candidates.forEach(([key, field]) => {
      this.autoAlignUndoSnapshot![key] = {
        x: this.toNumber(field.x, 0),
        y: this.toNumber(field.y, 0),
      };
    });

    const nextFields: Record<string, BlFieldConfig> = {
      ...this.fields,
    };

    const changedKeys = new Set<string>();

    const alignAxis = (
      axis: 'x' | 'y',
      tolerance: number
    ): void => {
      const sorted = candidates
        .map(([key, field]) => ({
          key,
          field,
          value: this.toNumber(field[axis], 0),
        }))
        .sort((a, b) => a.value - b.value);

      const groups: typeof sorted[] = [];
      let current: typeof sorted = [];

      sorted.forEach(item => {
        if (!current.length) {
          current = [item];
          return;
        }

        const center =
          current.reduce(
            (sum, entry) => sum + entry.value,
            0
          ) / current.length;

        if (Math.abs(item.value - center) <= tolerance) {
          current.push(item);
        } else {
          groups.push(current);
          current = [item];
        }
      });

      if (current.length) {
        groups.push(current);
      }

      groups
        .filter(group => group.length >= 2)
        .forEach(group => {
          const values = group
            .map(item => item.value)
            .sort((a, b) => a - b);

          const middle = Math.floor(values.length / 2);
          const target =
            values.length % 2
              ? values[middle]
              : (values[middle - 1] + values[middle]) / 2;

          const snapped = this.roundOneDecimal(target);

          group.forEach(item => {
            const existing =
              nextFields[item.key];

            if (!existing) return;

            if (
              Math.abs(
                this.toNumber(existing[axis], 0) -
                snapped
              ) < 0.05
            ) {
              return;
            }

            nextFields[item.key] = {
              ...existing,
              [axis]: snapped,
            };

            changedKeys.add(item.key);
          });
        });
    };

    // Vertical columns: same left edge.
    alignAxis('x', 24);

    // Horizontal rows: same baseline/top edge when already very close.
    alignAxis('y', 10);

    if (!changedKeys.size) {
      this.autoAlignUndoSnapshot = null;

      this.toastr.info(
        'Your ERP data keys are already aligned within print tolerance.',
        'Auto Align'
      );
      return;
    }

    Object.values(nextFields).forEach(field => {
      this.keepFieldInsidePage(field);
    });

    this.fields = nextFields;
    this.invalidateRenderCaches();

    if (this.viewLayouts) {
      this.commitCurrentViewLayout();
    }

    this.toastr.success(
      `${changedKeys.size} ERP data key${changedKeys.size === 1 ? '' : 's'} aligned for cleaner printing.`,
      'Auto Align Complete'
    );
  }

  undoAutoAlign(): void {
    if (!this.autoAlignUndoSnapshot) {
      return;
    }

    const nextFields: Record<string, BlFieldConfig> = {
      ...this.fields,
    };

    Object.entries(
      this.autoAlignUndoSnapshot
    ).forEach(([key, position]) => {
      const current = nextFields[key];

      if (!current) return;

      nextFields[key] = {
        ...current,
        x: position.x,
        y: position.y,
      };
    });

    this.fields = nextFields;
    this.autoAlignUndoSnapshot = null;
    this.invalidateRenderCaches();

    if (this.viewLayouts) {
      this.commitCurrentViewLayout();
    }

    this.toastr.info(
      'Previous manual positions restored.',
      'Auto Align Undone'
    );
  }


  updateField(
  key: string,
  property: EditableBlFieldProperty,
  value: any
): void {
  const currentField = this.fields[key];

  if (!currentField) {
    return;
  }

if (
  key === 'footer' &&
  (
    property === 'label' ||
    property === 'previewValue' ||
    property === 'showLabel' ||
    property === 'visible'
  )
) {
  return;
}


  const updatedField: BlFieldConfig = {
    ...currentField,
  };

  switch (property) {
    case 'label':
      updatedField.label = String(value ?? '');
      break;

    case 'previewValue':
  updatedField.previewValue =
    String(value ?? '');
  break;

    case 'x':
      updatedField.x = this.toNumber(value, currentField.x);
      break;

    case 'y':
      updatedField.y = this.toNumber(value, currentField.y);
      break;

    case 'width':
      updatedField.width = this.toNumber(value, currentField.width);
      break;

    case 'height':
      updatedField.height = this.toNumber(value, currentField.height);
      break;

    case 'fontSize':
      updatedField.fontSize = this.toNumber(value, currentField.fontSize);
      break;

    case 'fontFamily':
      updatedField.fontFamily =
        value === 'times' ||
        value === 'courier'
          ? value
          : 'helvetica';
      break;

    case 'fontWeight':
      updatedField.fontWeight =
        value === 'bold' ? 'bold' : 'normal';
      break;

    case 'fontStyle':
  updatedField.fontStyle =
    value === 'italic'
      ? 'italic'
      : 'normal';
  break;

case 'textDecoration':
  updatedField.textDecoration =
    value === 'underline' ||
    value === 'line-through'
      ? value
      : 'none';
  break;

case 'textAlign':
  updatedField.textAlign =
    value === 'center' ||
    value === 'right' ||
    value === 'justify'
      ? value
      : 'left';
  break;

    case 'lineHeight':
  updatedField.lineHeight =
    this.toNumber(
      value,
      currentField.lineHeight ?? 1.25
    );
  break;

  case 'borderTopWidth':
  updatedField.borderTopWidth =
    this.toNumber(value, 0);
  break;

case 'borderRightWidth':
  updatedField.borderRightWidth =
    this.toNumber(value, 0);
  break;

case 'borderBottomWidth':
  updatedField.borderBottomWidth =
    this.toNumber(value, 0);
  break;

case 'borderLeftWidth':
  updatedField.borderLeftWidth =
    this.toNumber(value, 0);
  break;

    case 'showLabel':
      updatedField.showLabel = Boolean(value);
      break;

    case 'visible':
      updatedField.visible = Boolean(value);
      break;

    case 'locked':
      updatedField.locked = Boolean(value);
      break;

case 'dataKey':
  updatedField.dataKey =
    String(value ?? '').trim();
  break;
  }


  if (key === 'footer') {
  updatedField.displayName = 'Footer';
  updatedField.type = 'text';
  updatedField.label = 'Footer';
  updatedField.dataKey = '';
  updatedField.previewValue =
    this.fixedFooterText;
  updatedField.showLabel = false;
  updatedField.visible = true;
  updatedField.borderWidth = 0;

updatedField.borderTopWidth = 0;
updatedField.borderRightWidth = 0;
updatedField.borderBottomWidth = 0;
updatedField.borderLeftWidth = 0;
}

  this.normalizeEditableField(updatedField);

  this.fields = {
    ...this.fields,
    [key]: updatedField,
  };

  this.activeFieldKey = key;
}




private normalizeEditableField(
  field: BlFieldConfig
): void {
  field.width = this.clamp(
    field.width,
    20,
    this.currentPageWidth,
    100
  );

  field.height = this.clamp(
    field.height,
    15,
    this.currentPageHeight,
    30
  );

  field.fontSize = this.clamp(
    field.fontSize,
    3,
    72,
    9
  );

  field.lineHeight = this.clamp(
  this.toNumber(
    field.lineHeight,
    1.25
  ),
  0.7,
  3,
  1.25
);

field.contentPadding = this.clamp(
  this.toNumber(
    field.contentPadding,
    7
  ),
  0,
  20,
  7
);

field.borderTopWidth = this.clamp(
  this.toNumber(
    field.borderTopWidth,
    field.borderWidth
  ),
  0,
  10,
  0
);

field.borderRightWidth = this.clamp(
  this.toNumber(
    field.borderRightWidth,
    field.borderWidth
  ),
  0,
  10,
  0
);

field.borderBottomWidth = this.clamp(
  this.toNumber(
    field.borderBottomWidth,
    field.borderWidth
  ),
  0,
  10,
  0
);

field.borderLeftWidth = this.clamp(
  this.toNumber(
    field.borderLeftWidth,
    field.borderWidth
  ),
  0,
  10,
  0
);

  field.x = this.clamp(
    field.x,
    0,
    Math.max(
      0,
      this.currentPageWidth - field.width
    ),
    0
  );

  field.y = this.clamp(
    field.y,
    0,
    Math.max(
      0,
      this.currentPageHeight - field.height
    ),
    0
  );
}

  startDrag(
    event: MouseEvent,
    key: string
  ): void {
    if (this.resizing) {
  return;
}
    const field = this.fields[key];

    if (!field || field.locked || !field.visible) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.activeFieldKey = key;

    this.dragging = {
      key,
      startClientX: event.clientX,
      startClientY: event.clientY,
      originalX: this.toNumber(field.x, 0),
      originalY: this.toNumber(field.y, 0),
    };

    document.addEventListener(
      'mousemove',
      this.onDragMove
    );

    document.addEventListener(
      'mouseup',
      this.stopDrag
    );
  }

  private dragFrameId:
  number | null = null;

private pendingDragPoint:
  {
    x: number;
    y: number;
  } | null = null;

onDragMove = (
  event: MouseEvent
): void => {

  if (!this.dragging) {
    return;
  }

  this.pendingDragPoint = {
    x: event.clientX,
    y: event.clientY,
  };

  if (
    this.dragFrameId !==
    null
  ) {
    return;
  }

  this.dragFrameId =
    requestAnimationFrame(
      () => {

        this.dragFrameId =
          null;

        const point =
          this.pendingDragPoint;

        const state =
          this.dragging;

        this.pendingDragPoint =
          null;

        if (
          !point ||
          !state
        ) {
          return;
        }

        const currentField =
          this.fields[
            state.key
          ];

        if (!currentField) {
          return;
        }

        const deltaX =
          point.x -
          state.startClientX;

        const deltaY =
          point.y -
          state.startClientY;

        const updatedField:
          BlFieldConfig = {
          ...currentField,

          x:
            this.roundOneDecimal(
              state.originalX +
              deltaX
            ),

          y:
            this.roundOneDecimal(
              state.originalY +
              deltaY
            ),
        };

        this.normalizeEditableField(
          updatedField
        );

        this.fields = {
          ...this.fields,

          [state.key]:
            updatedField,
        };
      }
    );
};

    stopDrag = (): void => {
    this.dragging = null;
    this.removeDragListeners();
  };

  startResize(
  event: MouseEvent,
  key: string,
  direction: ResizeDirection
): void {
  const field = this.fields[key];

  if (
    !field ||
    field.locked ||
    !field.visible
  ) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  // Ensure field movement is not running
  this.stopDrag();

  this.activeFieldKey = key;

  this.resizing = {
    key,
    direction,

    startClientX: event.clientX,
    startClientY: event.clientY,

    originalX: this.toNumber(field.x, 0),
    originalY: this.toNumber(field.y, 0),

    originalWidth: this.toNumber(
      field.width,
      100
    ),

    originalHeight: this.toNumber(
      field.height,
      30
    ),
  };

  document.addEventListener(
    'mousemove',
    this.onResizeMove
  );

  document.addEventListener(
    'mouseup',
    this.stopResize
  );
}

private resizeFrameId: number | null = null;
private pendingResizePoint: { x: number; y: number } | null = null;

onResizeMove = (
  event: MouseEvent
): void => {
  if (!this.resizing) return;
  event.preventDefault();

  this.pendingResizePoint = { x: event.clientX, y: event.clientY };
  if (this.resizeFrameId !== null) return;

  this.resizeFrameId = requestAnimationFrame(() => {
    this.resizeFrameId = null;

    const point = this.pendingResizePoint;
    const resizeState = this.resizing;
    this.pendingResizePoint = null;

    if (!point || !resizeState) return;

    const currentField = this.fields[resizeState.key];
    if (!currentField) return;

    const deltaX = point.x - resizeState.startClientX;
    const deltaY = point.y - resizeState.startClientY;
    const direction = resizeState.direction;
    const minWidth = 20;
    const minHeight = 15;

    let x = resizeState.originalX;
    let y = resizeState.originalY;
    let width = resizeState.originalWidth;
    let height = resizeState.originalHeight;

    const originalRight = resizeState.originalX + resizeState.originalWidth;
    const originalBottom = resizeState.originalY + resizeState.originalHeight;

    if (direction.includes('w')) {
      x = this.clamp(
        resizeState.originalX + deltaX,
        0,
        originalRight - minWidth,
        resizeState.originalX
      );
      width = originalRight - x;
    }

    if (direction.includes('e')) {
      width = this.clamp(
        resizeState.originalWidth + deltaX,
        minWidth,
        this.currentPageWidth - resizeState.originalX,
        resizeState.originalWidth
      );
    }

    if (direction.includes('n')) {
      y = this.clamp(
        resizeState.originalY + deltaY,
        0,
        originalBottom - minHeight,
        resizeState.originalY
      );
      height = originalBottom - y;
    }

    if (direction.includes('s')) {
      height = this.clamp(
        resizeState.originalHeight + deltaY,
        minHeight,
        this.currentPageHeight - resizeState.originalY,
        resizeState.originalHeight
      );
    }

    const updatedField: BlFieldConfig = {
      ...currentField,
      x: this.roundOneDecimal(x),
      y: this.roundOneDecimal(y),
      width: this.roundOneDecimal(width),
      height: this.roundOneDecimal(height),
    };

    this.normalizeEditableField(updatedField);
    this.fields = { ...this.fields, [resizeState.key]: updatedField };
  });
};

  stopResize = (): void => {
  this.resizing = null;
  this.pendingResizePoint = null;
  if (this.resizeFrameId !== null) {
    cancelAnimationFrame(this.resizeFrameId);
    this.resizeFrameId = null;
  }
  this.removeResizeListeners();
};

private removeResizeListeners(): void {
  document.removeEventListener(
    'mousemove',
    this.onResizeMove
  );

  document.removeEventListener(
    'mouseup',
    this.stopResize
  );
}

  getPageStyle(): Record<string, string> {
    const original =
      this.isOriginalView();

    /*
     * CRITICAL PERFORMANCE RULE:
     * Never render the full Firestore Base64 string in the live Angular
     * designer. The canvas stays white for the few milliseconds required to
     * build editorBackgroundPreviewUrl, then only the lightweight object URL
     * is painted.
     */
    const backgroundImageUrl =
      original
        ? ''
        : String(
            this.editorBackgroundPreviewUrl ||
            ''
          ).trim();

    const width = this.currentPageWidth;
    const height = this.currentPageHeight;

    const backgroundColor =
      String(
        this.form.backgroundColor ||
        '#ffffff'
      );

    const textColor =
      String(
        this.form.textColor ||
        '#111827'
      );

    const cacheKey = [
      width,
      height,
      backgroundImageUrl,
      backgroundColor,
      textColor,
      original ? '1' : '0',
    ].join('|');

    if (
      cacheKey === this.pageStyleCacheKey
    ) {
      return this.pageStyleCache;
    }

    this.pageStyleCacheKey = cacheKey;

    this.pageStyleCache = {
      width: `${width}px`,
      height: `${height}px`,
      backgroundColor,
      backgroundImage: backgroundImageUrl
        ? `url("${backgroundImageUrl}")`
        : 'none',
      color: textColor,
      backgroundSize: '100% 100%',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',

      // Keep paint isolated without permanently promoting this large page
      // to a GPU layer. Permanent will-change/translateZ can make long-page
      // scrolling worse on some Chrome/Windows GPUs.
      contain: 'layout paint',
    };

    return this.pageStyleCache;
  }

  getFieldBorderWidth(
  field: BlFieldConfig,
  side: BlBorderSide
): number {
  const fallback =
    this.toNumber(
      field.borderWidth,
      0
    );

  switch (side) {
    case 'top':
      return this.toNumber(
        field.borderTopWidth,
        fallback
      );

    case 'right':
      return this.toNumber(
        field.borderRightWidth,
        fallback
      );

    case 'bottom':
      return this.toNumber(
        field.borderBottomWidth,
        fallback
      );

    case 'left':
      return this.toNumber(
        field.borderLeftWidth,
        fallback
      );
  }
}

  getFieldStyle(key: string): Record<string, string> {
    const field = this.fields[key];

    if (!field) {
      return {};
    }

    const globalOffsetX =
      this.toNumber(
        this.form.globalOffsetX,
        0
      );

    const globalOffsetY =
      this.toNumber(
        this.form.globalOffsetY,
        0
      );

    const original =
      this.isOriginalView();

    const textColor =
      String(
        this.form.textColor ||
        '#111827'
      );

    const cached =
      this.fieldStyleCache.get(key);

    if (
      cached &&
      cached.field === field &&
      cached.offsetX === globalOffsetX &&
      cached.offsetY === globalOffsetY &&
      cached.original === original &&
      cached.textColor === textColor
    ) {
      return cached.style;
    }

    const padding =
      this.toNumber(
        field.contentPadding,
        7
      );

    const style: Record<string, string> = {
      left:
        `${this.toNumber(field.x, 0) + globalOffsetX}px`,
      top:
        `${this.toNumber(field.y, 0) + globalOffsetY}px`,
      width:
        `${this.toNumber(field.width, 100)}px`,
      height:
        `${this.toNumber(field.height, 30)}px`,
      fontSize:
        `${this.toNumber(field.fontSize, 9)}px`,
      fontFamily:
        this.getCssFontFamily(field),
      lineHeight:
        String(
          this.toNumber(
            field.lineHeight,
            1.25
          )
        ),
      fontWeight:
        field.fontWeight || 'normal',
      fontStyle:
        field.fontStyle || 'normal',
      textDecoration:
        field.textDecoration || 'none',
      textAlign:
        field.textAlign || 'left',
      backgroundColor:
        original
          ? 'transparent'
          : (
              field.backgroundColor ||
              'transparent'
            ),
      backgroundImage: 'none',
      backgroundSize: 'auto',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      color:
        original
          ? textColor
          : (
              field.textColor ||
              textColor
            ),
      borderTopWidth:
        `${original ? 0 : this.getFieldBorderWidth(field, 'top')}px`,
      borderRightWidth:
        `${original ? 0 : this.getFieldBorderWidth(field, 'right')}px`,
      borderBottomWidth:
        `${original ? 0 : this.getFieldBorderWidth(field, 'bottom')}px`,
      borderLeftWidth:
        `${original ? 0 : this.getFieldBorderWidth(field, 'left')}px`,
      borderStyle: 'solid',
      borderColor: '#111827',
      boxSizing: 'border-box',
      padding: `${padding}px`,
      overflow:
        (
          field.type === 'text' &&
          padding <= 1
        )
          ? 'visible'
          : 'hidden',
      whiteSpace:
        field.type === 'container-table'
          ? 'pre-line'
          : 'normal',
      outline: 'none',
      outlineOffset: '0',
      zIndex:
        String(
          this.toNumber(
            field.zIndex,
            10
          )
        ),
      cursor:
        field.locked
          ? 'not-allowed'
          : 'move',
      contain: 'layout paint style',
    };

    this.fieldStyleCache.set(
      key,
      {
        field,
        offsetX: globalOffsetX,
        offsetY: globalOffsetY,
        original,
        textColor,
        style,
      }
    );

    return style;
  }

  getPreviewValue(key: string): string {
    const field = this.fields[key];

    if (!field) {
      return '';
    }


    if (key === 'footer') {
  return this.fixedFooterText;
}

    if (this.isDocumentTypeField(key, field)) {
    return this.getDocumentTypeText();
  }

  if (this.isImportedStructureField(key, field)) {
    return '';
  }

  switch (field.type) {
      case 'company-logo':
        return '';

      case 'digital-stamp':
        return 'DIGITAL STAMP';

      case 'container-table':
        return [
          'CONT0012345 | SEAL001 | 1200 KG | 25 | 18.50',
          'CONT0067890 | SEAL002 | 1350 KG | 30 | 20.10',
        ].join('\n');

      case 'company-name':
        return this.company.name || 'Company Name';

      case 'company-address':
        return this.company.address || 'Company Address';

      case 'company-contact':
        return [
          this.company.telephone
            ? `Tel: ${this.company.telephone}`
            : '',

          this.company.email
            ? `Email: ${this.company.email}`
            : '',

          this.company.ntn
            ? `NTN: ${this.company.ntn}`
            : '',
        ]
          .filter(Boolean)
          .join(' | ');

      case 'text':
        return (
          field.previewValue ||
          field.label ||
          'Static Text'
        );

      case 'field':
      default:
        if (field.dataKey === 'company.phone') {
          return this.company.telephone || '{{company.phone}}';
        }
        if (field.dataKey === 'company.email') {
          return this.company.email || '{{company.email}}';
        }
        if (field.dataKey === 'company.ntn') {
          return this.company.ntn || '{{company.ntn}}';
        }

        return (
          String(field.previewValue || '').trim() ||
          (field.dataKey
            ? `{{${field.dataKey}}}`
            : '')
        );
    }
  }

  getFieldDisplayName(key: string): string {
  const names: Record<string, string> = {
    consignor: 'Consignor',
    consignorAddress: 'Consignor Address',
    companyLogo: 'Company Logo',
    digitalStamp: 'Digital Stamp',
    companyName: 'Company Name',
    companyAddress: 'Company Address',
    companyContact: 'Company Contact',
    consignee: 'Consignee',
    consigneeAddress: 'Consignee Address',
    blNumber: 'BL / MTD Number',
    notifyParty: 'Notify Party',
    deliveryContact: 'Delivery Contact',
    placeOfAcceptance: 'Place of Acceptance',
    portOfLoading: 'Port of Loading',
    portOfDischarge: 'Port of Discharge',
    vesselVoyage: 'Vessel / Voyage',
    placeOfDelivery: 'Place of Delivery',
    modeOfTransport: 'Mode of Transport',
    shipmentType: 'Shipment Type (FCL / LCL)',
    containerMarks: 'Container / Seal / Marks',
    goodsDescription: 'Description of Goods',
    numberOfPackages: 'Packages',
    grossWeight: 'Gross Weight',
    measurement: 'Measurement',
    freightAmount: 'Freight Amount',
    issuePlaceDate: 'Place / Date of Issue',
    authorisedSignatory: 'Authorised Signatory',
    footer: 'Footer',
  };

  if (this.isCustomField(key)) {
  return (
    this.fields[key]?.displayName ||
    this.fields[key]?.label ||
    'Custom Field'
  );
}

return (
  names[key] ||
  this.fields[key]?.displayName ||
  this.fields[key]?.label ||
  key
);
}

  trackByPaletteItem(_index: number, item: BlDataKeyPaletteItem): string {
    return item.dataKey;
  }

  trackByTemplateId(_index: number, template: BlTemplate): string {
    return template?.id || String(_index);
  }

  trackByFieldKey(
    index: number,
    key: string
  ): string {
    return key;
  }

  private prepareFieldsForSave(): Record<
    string,
    BlFieldConfig
  > {
    const result: Record<string, BlFieldConfig> = {};

    Object.entries(this.fields).forEach(
      ([key, currentField], index) => {
        const field = this.normalizeField(
          key,
          currentField,
          index
        );

        if (
          field.type !== 'company-logo' &&
          !String(field.label || '').trim()
        ) {
          field.label = 'Untitled Field';
        }

        result[key] = field;
      }
    );

    return this.enforceFixedFooter(result);
  }

  private normalizeTemplate(
    id: string,
    saved: any
  ): BlTemplate {
    const fallbackPage: Partial<BlViewLayout> = {
      pageWidthPx: this.toNumber(saved?.pageWidthPx, 794),
      pageHeightPx: this.toNumber(saved?.pageHeightPx, 1128),
      globalOffsetX: this.toNumber(saved?.globalOffsetX, 0),
      globalOffsetY: this.toNumber(saved?.globalOffsetY, 0),
      backgroundImageUrl: saved?.backgroundImageUrl || '',
      backgroundColor: saved?.backgroundColor || '#ffffff',
      textColor: saved?.textColor || '#111827',
    };

    const legacyFields = this.normalizeFields(
      saved?.fields &&
      Object.keys(saved.fields).length
        ? saved.fields
        : this.getDefaultFields()
    );

    const nonNegotiableLayout = this.normalizeViewLayout(
      saved?.viewLayouts?.nonNegotiable,
      legacyFields,
      fallbackPage
    );

    const originalLayout = saved?.viewLayouts?.original
      ? this.normalizeViewLayout(
          saved.viewLayouts.original,
          legacyFields,
          fallbackPage
        )
      : this.createOriginalLayoutFromNonNegotiable(
          nonNegotiableLayout
        );

    return {
      id,
      companyId:
        saved?.companyId || this.companyId,

      templateName:
        saved?.templateName || 'BL Template',

      // Legacy page settings mirror Non-Negotiable for backward compatibility.
      pageWidthPx: nonNegotiableLayout.pageWidthPx,
      pageHeightPx: nonNegotiableLayout.pageHeightPx,
      globalOffsetX: nonNegotiableLayout.globalOffsetX,
      globalOffsetY: nonNegotiableLayout.globalOffsetY,
      backgroundImageUrl: nonNegotiableLayout.backgroundImageUrl,
      sourceTraceImageUrl: String(saved?.sourceTraceImageUrl || '').trim(),
      backgroundColor: nonNegotiableLayout.backgroundColor,
      textColor: nonNegotiableLayout.textColor,

      isDefault: Boolean(saved?.isDefault),
      isActive: saved?.isActive !== false,

      fields: this.clone(nonNegotiableLayout.fields),
      viewLayouts: {
        nonNegotiable: nonNegotiableLayout,
        original: originalLayout,
      },

      views: this.templateViews,
      defaultViewMode:
        saved?.defaultViewMode === 'original'
          ? 'original'
          : 'non-negotiable',

      createdAt: saved?.createdAt,
      updatedAt: saved?.updatedAt,
    };
  }

  private normalizeFields(
    source: Record<string, any>
  ): Record<string, BlFieldConfig> {
    const result: Record<string, BlFieldConfig> = {};

    Object.entries(source || {}).forEach(
      ([key, field], index) => {
        result[key] = this.normalizeField(
          key,
          field,
          index
        );
      }
    );

/*
 * Manual B/L designer rule:
 * Do NOT silently inject Digital Stamp (or any ERP field) while normalizing.
 * Every ERP overlay must exist only because the user explicitly placed it.
 */
return this.enforceFixedFooter(result);
  }

  private normalizeField(
    key: string,
    source: any,
    index: number
  ): BlFieldConfig {
    if (!this.defaultFieldsCache) {
      this.defaultFieldsCache = this.getDefaultFields();
    }
    const fallback = this.defaultFieldsCache[key];

    const resolvedType: BlFieldType =
      key === 'companyLogo'
        ? 'company-logo'
        : key === 'digitalStamp'
          ? 'digital-stamp'
        : key === 'companyName'
          ? 'company-name'
          : key === 'companyAddress'
            ? 'company-address'
            : key === 'companyContact'
              ? 'company-contact'
              : source?.type ||
                fallback?.type ||
                'field';

    const legacyBorderWidth =
  this.toNumber(
    source?.borderWidth,
    fallback?.borderWidth ?? 1
  );

    const field: BlFieldConfig = {
      key,

      displayName:
  source?.displayName ??
  fallback?.displayName ??
  source?.label ??
  fallback?.label ??
  key,
      type: resolvedType,

      label:
        source?.label ??
        fallback?.label ??
        key,

      dataKey:
        source?.dataKey ??
        fallback?.dataKey ??
        '',

      previewValue:
        source?.previewValue ??
        fallback?.previewValue ??
        '',

      // Legacy source-crop fields are upgraded to real editable text.
      sourceVisual: false,
      sourceCropX: this.toNumber(source?.sourceCropX, fallback?.sourceCropX ?? source?.x ?? 0),
      sourceCropY: this.toNumber(source?.sourceCropY, fallback?.sourceCropY ?? source?.y ?? 0),
      sourceCropWidth: this.toNumber(source?.sourceCropWidth, fallback?.sourceCropWidth ?? source?.width ?? 1),
      sourceCropHeight: this.toNumber(source?.sourceCropHeight, fallback?.sourceCropHeight ?? source?.height ?? 1),

      x: this.toNumber(
        source?.x,
        fallback?.x ?? 20
      ),

      y: this.toNumber(
        source?.y,
        fallback?.y ?? 20
      ),

      width: this.toNumber(
        source?.width,
        fallback?.width ?? 150
      ),

      height: this.toNumber(
        source?.height,
        fallback?.height ?? 45
      ),

      fontSize: this.toNumber(
        source?.fontSize,
        fallback?.fontSize ?? 9
      ),

      fontFamily:
        source?.fontFamily === 'times' ||
        source?.fontFamily === 'courier'
          ? source.fontFamily
          : fallback?.fontFamily || 'helvetica',

      lineHeight: this.toNumber(
  source?.lineHeight,
  fallback?.lineHeight ?? 1.25
),

      contentPadding: this.clamp(
        this.toNumber(
          source?.contentPadding,
          fallback?.contentPadding ?? 7
        ),
        0,
        20,
        7
      ),

      fontWeight:
        source?.fontWeight === 'bold'
          ? 'bold'
          : source?.fontWeight === 'normal'
            ? 'normal'
            : String(
                source?.dataKey ??
                fallback?.dataKey ??
                ''
              ).trim()
              ? 'normal'
              : fallback?.fontWeight || 'normal',

     textAlign:
  source?.textAlign === 'center' ||
  source?.textAlign === 'right' ||
  source?.textAlign === 'justify'
    ? source.textAlign
    : fallback?.textAlign || 'left',

    fontStyle:
  source?.fontStyle === 'italic'
    ? 'italic'
    : fallback?.fontStyle || 'normal',

    textDecoration:
  source?.textDecoration === 'underline' ||
  source?.textDecoration === 'line-through'
    ? source.textDecoration
    : fallback?.textDecoration || 'none',

    backgroundColor:
      String(
        source?.backgroundColor ??
        fallback?.backgroundColor ??
        ''
      ).trim(),

    textColor:
      String(
        source?.textColor ??
        fallback?.textColor ??
        ''
      ).trim(),

      showLabel:
        resolvedType === 'company-logo' ||
        resolvedType === 'digital-stamp' ||
        resolvedType === 'container-table' ||
        resolvedType === 'company-name' ||
        resolvedType === 'company-address' ||
        resolvedType === 'company-contact'
          ? false
          : source?.showLabel ??
            fallback?.showLabel ??
            true,

      visible:
        source?.visible ??
        fallback?.visible ??
        true,

      locked:
        source?.locked ??
        fallback?.locked ??
        false,

      borderWidth: legacyBorderWidth,

borderTopWidth: this.toNumber(
  source?.borderTopWidth,
  fallback?.borderTopWidth ??
    legacyBorderWidth
),

borderRightWidth: this.toNumber(
  source?.borderRightWidth,
  fallback?.borderRightWidth ??
    legacyBorderWidth
),

borderBottomWidth: this.toNumber(
  source?.borderBottomWidth,
  fallback?.borderBottomWidth ??
    legacyBorderWidth
),

borderLeftWidth: this.toNumber(
  source?.borderLeftWidth,
  fallback?.borderLeftWidth ??
    legacyBorderWidth
),

      containerTableShowHeader:
        resolvedType === 'container-table'
          ? true
          : (
              source?.containerTableShowHeader ??
              fallback?.containerTableShowHeader ??
              false
            ),

      containerTableShowBorders:
        source?.containerTableShowBorders ??
        fallback?.containerTableShowBorders ??
        false,

      containerTableHeaderHeight: this.toNumber(
        source?.containerTableHeaderHeight,
        fallback?.containerTableHeaderHeight ?? 22
      ),

      containerTableRowHeight: this.toNumber(
        source?.containerTableRowHeight,
        fallback?.containerTableRowHeight ?? 22
      ),

      containerTableColumns:
        Array.isArray(source?.containerTableColumns) && source.containerTableColumns.length
          ? this.clone(source.containerTableColumns)
          : Array.isArray(fallback?.containerTableColumns) && fallback.containerTableColumns.length
            ? this.clone(fallback.containerTableColumns)
            : undefined,

      zIndex: this.toNumber(
        source?.zIndex,
        fallback?.zIndex ?? index + 10
      ),
    };

    if (resolvedType === 'container-table') {
      const existingColumns = Array.isArray(field.containerTableColumns)
        ? field.containerTableColumns
        : [];

      const widthByKey = new Map<string, number>();
      existingColumns.forEach((column: any) => {
        const columnKey = String(column?.key || '').trim();
        if (!columnKey) return;

        widthByKey.set(
          columnKey,
          Math.max(1, this.toNumber(column?.width, 1))
        );
      });

      field.containerTableShowHeader = true;
      field.containerTableColumns = [
        { key: 'containerNo', label: 'Container', width: widthByKey.get('containerNo') || 24 },
        { key: 'sealNo', label: 'Seal', width: widthByKey.get('sealNo') || 15 },
        { key: 'shipmentType', label: 'Type', width: widthByKey.get('shipmentType') || 12 },
        { key: 'grossWeight', label: 'GR WT', width: widthByKey.get('grossWeight') || 14 },
        { key: 'netWeight', label: 'NT WT', width: widthByKey.get('netWeight') || 14 },
        { key: 'packages', label: 'PCKGS', width: widthByKey.get('packages') || 16 },
        { key: 'cbm', label: 'CBM', width: widthByKey.get('cbm') || 12 },
      ];
    }

    // Firestore rejects `undefined` anywhere inside nested objects.
    // Only container-table fields should persist column configuration.
    if (
      resolvedType !== 'container-table' ||
      !Array.isArray(field.containerTableColumns) ||
      field.containerTableColumns.length === 0
    ) {
      delete (field as any).containerTableColumns;
    }

    if (
  resolvedType === 'text'
) {
  field.dataKey = '';
}

if (
  key === 'footer'
) {
  field.displayName =
    'Footer';

  field.type = 'text';
  field.label = 'Footer';
  field.dataKey = '';

  field.previewValue =
    this.fixedFooterText;

  field.showLabel = false;
  field.visible = true;

  field.borderWidth = 0;
  field.borderTopWidth = 0;
field.borderRightWidth = 0;
field.borderBottomWidth = 0;
field.borderLeftWidth = 0;
}


    this.keepFieldInsidePage(field);

    return field;
  }

  private getDefaultFields(): Record<
  string,
  BlFieldConfig
> {
  const fields: Record<
    string,
    BlFieldConfig
  > = {};

  let generatedZIndex = 10;

  const add = (
    field: Omit<
      BlFieldConfig,
      | 'displayName'
      | 'visible'
      | 'locked'
      | 'zIndex'
    > &
      Partial<
        Pick<
          BlFieldConfig,
          | 'displayName'
          | 'visible'
          | 'locked'
          | 'zIndex'
        >
      >
  ): void => {
    const automaticZIndex =
  generatedZIndex++;

const legacyBorderWidth =
  this.toNumber(
    field.borderWidth,
    0
  );

fields[field.key] = {
  ...field,

  displayName:
    field.displayName ||
    field.label ||
    field.key,

  /*
   * Every ERP/runtime dataKey defaults to NORMAL.
   * Static headings/text may still keep their own bold formatting.
   */
  fontWeight:
    String(field.dataKey || '').trim()
      ? 'normal'
      : (field.fontWeight || 'normal'),

  /*
   * Default text formatting
   */
  fontStyle:
    field.fontStyle ||
    'normal',

  textDecoration:
    field.textDecoration ||
    'none',

  lineHeight:
    this.toNumber(
      field.lineHeight,
      1.25
    ),

  /*
   * Convert old common borderWidth
   * into four individual borders.
   */
  borderTopWidth:
    this.toNumber(
      field.borderTopWidth,
      legacyBorderWidth
    ),

  borderRightWidth:
    this.toNumber(
      field.borderRightWidth,
      legacyBorderWidth
    ),

  borderBottomWidth:
    this.toNumber(
      field.borderBottomWidth,
      legacyBorderWidth
    ),

  borderLeftWidth:
    this.toNumber(
      field.borderLeftWidth,
      legacyBorderWidth
    ),

  visible:
    field.visible ?? true,

  locked:
    field.locked ?? false,

  zIndex:
    field.zIndex ??
    automaticZIndex,
};
  };



  const addStaticText = (
    config: {
      key: string;
      displayName: string;
      text: string;

      x: number;
      y: number;
      width: number;
      height: number;

      fontSize?: number;
      fontWeight?: BlFontWeight;
      textAlign?: BlTextAlign;

      borderWidth?: number;
      zIndex?: number;
      locked?: boolean;
    }


  ): void => {
    add({
      key: config.key,

      displayName:
        config.displayName,

      type: 'text',



      label:
        config.displayName,

      dataKey: '',

      previewValue:
        config.text,

      x: config.x,
      y: config.y,

      width:
        config.width,

      height:
        config.height,

      fontSize:
        config.fontSize ??
        8,

      // Dynamic ERP values are normal by default.
      fontWeight: 'normal',

      textAlign:
        config.textAlign ??
        'left',

      showLabel: false,

      borderWidth:
        config.borderWidth ??
        0,

      zIndex:
        config.zIndex,

      locked:
        config.locked,
    });
  };

  const addDynamicField = (
    config: {
      key: string;
      displayName: string;
      label: string;
      dataKey: string;

      x: number;
      y: number;
      width: number;
      height: number;

      fontSize?: number;
      fontWeight?: BlFontWeight;
      textAlign?: BlTextAlign;

      showLabel?: boolean;
      borderWidth?: number;
      zIndex?: number;
    }
  ): void => {
    add({
      key: config.key,

      displayName:
        config.displayName,

      type: 'field',

      label:
        config.label,

      dataKey:
        config.dataKey,

      previewValue: '',

      x: config.x,
      y: config.y,

      width:
        config.width,

      height:
        config.height,

      fontSize:
        config.fontSize ??
        9,

      fontWeight:
        config.fontWeight ??
        'normal',

      textAlign:
        config.textAlign ??
        'left',

      showLabel:
        config.showLabel ??
        true,

      borderWidth:
        config.borderWidth ??
        1,

      zIndex:
        config.zIndex,
    });
  };

  /*
   * ==========================================
   * TOP LEFT: CONSIGNOR
   * ==========================================
   */

  addDynamicField({
    key: 'consignor',

    displayName:
      'Consignor',

    label:
      'Consignor',

    dataKey:
      'consignor.name',

    x: 36,
    y: 11,

    width: 350,
    height: 145,

    fontSize: 9,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,

    zIndex: 10,
  });

  /*
   * ==========================================
   * TOP RIGHT: COMPANY HEADER
   * ==========================================
   */

  addStaticText({
    key:
      'companyHeaderBox',

    displayName:
      'Company Header Box',

    text: ' ',

    x: 386,
    y: 11,

    width: 391,
    height: 145,

    borderWidth: 1,
    zIndex: 1,

    locked: true,
  });

  add({
    key: 'companyLogo',

    displayName:
      'Company Logo',

    type:
      'company-logo',

    label:
      'Company Logo',

    dataKey:
      'company.logo',

    previewValue: '',

    x: 397,
    y: 23,

    width: 106,
    height: 53,

    fontSize: 9,
    fontWeight: 'normal',
    textAlign: 'center',

    showLabel: false,
    borderWidth: 0,

    zIndex: 30,
  });

  add({
    key: 'digitalStamp',

    displayName:
      'Digital Stamp',

    type:
      'digital-stamp',

    label:
      'Digital Stamp',

    dataKey:
      'digitalStamp',

    previewValue:
      'DIGITAL STAMP',

    x: 500,
    y: 820,

    width: 180,
    height: 90,

    fontSize: 13,
    fontWeight: 'bold',
    textAlign: 'center',

    showLabel: false,
    borderWidth: 0,
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,

    zIndex: 95,
  });

  add({
    key: 'companyName',

    displayName:
      'Company Name',

    type:
      'company-name',

    label:
      'Company Name',

    dataKey:
      'company.name',

    previewValue: '',

    x: 500,
    y: 27,

    width: 262,
    height: 30,

    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center',

    showLabel: false,
    borderWidth: 0,

    zIndex: 31,
  });

  add({
    key: 'companyAddress',

    displayName:
      'Company Address',

    type:
      'company-address',

    label:
      'Company Address',

    dataKey:
      'company.address',

    previewValue: '',

    x: 505,
    y: 61,

    width: 257,
    height: 20,

    fontSize: 5,
    fontWeight: 'bold',
    textAlign: 'right',

    showLabel: false,
    borderWidth: 0,

    zIndex: 31,
  });

  add({
    key: 'companyContact',

    displayName:
      'Company Contact',

    type:
      'company-contact',

    label:
      'Company Contact',

    dataKey:
      'company.contact',

    previewValue: '',

    x: 492,
    y: 82,

    width: 272,
    height: 28,

    fontSize: 5,
    fontWeight: 'bold',
    textAlign: 'right',

    showLabel: false,
    borderWidth: 0,

    zIndex: 31,
  });

  addStaticText({
    key:
      'aboveTradeName',

    displayName:
      'Trade Name Notice',

    text:
      'Above represents the trade name',

    x: 397,
    y: 136,

    width: 365,
    height: 16,

    fontSize: 5,
    fontWeight: 'bold',

    textAlign: 'left',

    zIndex: 31,
  });

  /*
   * ==========================================
   * CONSIGNEE
   * ==========================================
   */

  addDynamicField({
    key: 'consignee',

    displayName:
      'Consignee',

    label:
      'Consignee (or order)',

    dataKey:
      'consignee.name',

    x: 36,
    y: 156,

    width: 350,
    height: 181,

    fontSize: 9,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,

    zIndex: 10,
  });

  /*
   * ==========================================
   * BILL OF LADING INFORMATION BOX
   * ==========================================
   */

  addStaticText({
    key:
      'billInformationBox',

    displayName:
      'Bill Information Box',

    text: ' ',

    x: 386,
    y: 156,

    width: 391,
    height: 181,

    borderWidth: 1,

    zIndex: 1,
    locked: true,
  });

  addStaticText({
    key:
      'billOfLadingTitle',

    displayName:
      'Bill of Lading Title',

    text:
      'Bill of Lading',

    x: 386,
    y: 166,

    width: 391,
    height: 18,

    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',

    zIndex: 30,
  });

  addStaticText({
    key:
      'multimodalTitle',

    displayName:
      'Multimodal Title',

    text:
      'Multimodal Transport Document',

    x: 386,
    y: 184,

    width: 391,
    height: 16,

    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center',

    zIndex: 30,
  });

  addDynamicField({
    key: 'blNumber',

    displayName:
      'BL / MTD Number',

    label:
      'BL/MTD Number:',

    dataKey:
      'blNumber',

    x: 398,
    y: 202,

    width: 367,
    height: 42,

    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'center',

    showLabel: true,
    borderWidth: 0,

    zIndex: 31,
  });

  addStaticText({
    key:
      'billLegalTerms',

    displayName:
      'Bill Legal Terms',

    text:
`Taken in charge in apparently good condition herein at the place of receipt for transport and delivery as
mentioned above unless otherwise stated. The MTO in accordance with the provision contained in the MTD
undertakes to perform or to procure the performance of the multimodal transport from the place at which the
goods are taken in charge, to the place designated for delivery & assume responsibility for such transport.

One of the MTD(s) must be surrendered, duly endorsed exchange for the goods in witness where of the original
MTD all of this tenor & date have been signed in the number indicated below one of which being accomplished
the other(s) to be void.`,

    x: 397,
    y: 245,

    width: 365,
    height: 82,

    fontSize: 4,
    fontWeight: 'normal',
    textAlign: 'left',

    zIndex: 30,
  });

  /*
   * ==========================================
   * NOTIFY / DELIVERY CONTACT
   * ==========================================
   */

  addDynamicField({
    key: 'notifyParty',

    displayName:
      'Notify Party / Address',

    label:
      'Notify Address',

    dataKey:
      'notifyParty.fullText',

    x: 36,
    y: 337,

    width: 350,
    height: 154,

    fontSize: 8,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,

    zIndex: 10,
  });

  addDynamicField({
    key:
      'deliveryContact',

    displayName:
      'Delivery Contact',

    label:
      'To Obtain Delivery Contact',

    dataKey:
      'deliveryContact',

    x: 386,
    y: 337,

    width: 391,
    height: 154,

    fontSize: 8,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,

    zIndex: 10,
  });

  /*
   * ==========================================
   * ROUTING ROW 1
   * ==========================================
   */

  addDynamicField({
    key:
      'placeOfAcceptance',

    displayName:
      'Place of Acceptance',

    label:
      'Place of Acceptance',

    dataKey:
      'placeOfAcceptance',

    x: 36,
    y: 491,

    width: 121,
    height: 47,

    fontSize: 7,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'acceptanceDate',

    displayName:
      'Date of Acceptance',

    label:
      'Date of Acceptance',

    dataKey:
      'acceptanceDate',

    x: 157,
    y: 491,

    width: 121,
    height: 47,

    fontSize: 7,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'portOfLoading',

    displayName:
      'Port of Loading',

    label:
      'Port of Loading',

    dataKey:
      'portOfLoading',

    x: 278,
    y: 491,

    width: 499,
    height: 47,

    fontSize: 7,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,
  });

  /*
   * ==========================================
   * ROUTING ROW 2
   * ==========================================
   */

  addDynamicField({
    key:
      'portOfDischarge',

    displayName:
      'Port of Discharge',

    label:
      'Port of Discharge',

    dataKey:
      'portOfDischarge',

    x: 36,
    y: 538,

    width: 181,
    height: 47,

    fontSize: 7,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'placeOfDelivery',

    displayName:
      'Place of Delivery',

    label:
      'Place of Delivery',

    dataKey:
      'placeOfDelivery',

    x: 217,
    y: 538,

    width: 169,
    height: 47,

    fontSize: 7,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,
  });

  addStaticText({
    key:
      'routeRowBlankArea',

    displayName:
      'Route Row Blank Area',

    text: ' ',

    x: 386,
    y: 538,

    width: 391,
    height: 47,

    borderWidth: 1,

    zIndex: 1,
    locked: true,
  });

  /*
   * ==========================================
   * ROUTING ROW 3
   * ==========================================
   */

  addDynamicField({
    key:
      'vesselVoyage',

    displayName:
      'Vessel / Voyage',

    label:
      'Vessel Voy No.',

    dataKey:
      'vesselVoyage',

    x: 36,
    y: 585,

    width: 178,
    height: 48,

    fontSize: 7,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'deliveryPeriod',

    displayName:
      'Delivery Period',

    label:
      'Date or Period of Delivery',

    dataKey:
      'deliveryPeriod',

    x: 214,
    y: 585,

    width: 177,
    height: 48,

    fontSize: 7,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'modeOfTransport',

    displayName:
      'Mode of Transport',

    label:
      'Modes / Means of Transport',

    dataKey:
      'modeOfTransport',

    x: 391,
    y: 585,

    width: 178,
    height: 48,

    fontSize: 7,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,
  });

  addDynamicField({
    key: 'route',

    displayName:
      'Transshipment Route',

    label:
      'Route/Place of Transshipment(if any)',

    dataKey:
      'route',

    x: 569,
    y: 585,

    width: 208,
    height: 48,

    fontSize: 6,
    fontWeight: 'bold',

    showLabel: true,
    borderWidth: 1,
  });

  /*
   * ==========================================
   * CARGO TABLE HEADERS
   * ==========================================
   */

  addStaticText({
    key:
      'containerMarksHeading',

    displayName:
      'Container Marks Heading',

    text:
      'Container No / SealNo Marks & Numbers',

    x: 36,
    y: 633,

    width: 234,
    height: 41,

    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center',

    borderWidth: 1,
  });

  addStaticText({
    key:
      'goodsDescriptionHeading',

    displayName:
      'Goods Description Heading',

    text:
`Number of packages, kinds of packages,
general description of goods (said to contain)`,

    x: 270,
    y: 633,

    width: 272,
    height: 41,

    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center',

    borderWidth: 1,
  });

  addStaticText({
    key:
      'packagesHeading',

    displayName:
      'Packages Heading',

    text:
      'No of Packages',

    x: 542,
    y: 633,

    width: 80,
    height: 41,

    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center',

    borderWidth: 1,
  });

  addStaticText({
    key:
      'grossWeightHeading',

    displayName:
      'Gross Weight Heading',

    text:
      'Gross Weight',

    x: 622,
    y: 633,

    width: 79,
    height: 41,

    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center',

    borderWidth: 1,
  });

  addStaticText({
    key:
      'measurementHeading',

    displayName:
      'Measurement Heading',

    text:
      'Measurement',

    x: 701,
    y: 633,

    width: 76,
    height: 41,

    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center',

    borderWidth: 1,
  });

  /*
   * ==========================================
   * CARGO TABLE VALUES
   * ==========================================
   */

  addDynamicField({
    key:
      'containerMarks',

    displayName:
      'Container / Seal / Marks',

    label:
      'Container / Seal / Marks',

    dataKey:
      'containerMarks',

    x: 36,
    y: 674,

    width: 234,
    height: 207,

    fontSize: 7,
    fontWeight: 'bold',

    showLabel: false,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'goodsDescription',

    displayName:
      'Description of Goods',

    label:
      'Description of Goods',

    dataKey:
      'goodsDescription',

    x: 270,
    y: 674,

    width: 272,
    height: 207,

    fontSize: 7,
    fontWeight: 'bold',

    showLabel: false,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'numberOfPackages',

    displayName:
      'Packages',

    label:
      'Packages',

    dataKey:
      'numberOfPackages',

    x: 542,
    y: 674,

    width: 80,
    height: 207,

    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',

    showLabel: false,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'grossWeight',

    displayName:
      'Gross / Net Weight',

    label:
      'Gross Weight',

    dataKey:
      'grossWeight',

    x: 622,
    y: 674,

    width: 79,
    height: 207,

    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',

    showLabel: false,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'measurement',

    displayName:
      'Measurement',

    label:
      'Measurement',

    dataKey:
      'measurement',

    x: 701,
    y: 674,

    width: 76,
    height: 207,

    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',

    showLabel: false,
    borderWidth: 1,
  });

  /*
   * ==========================================
   * SOB ROW
   * ==========================================
   */

  addStaticText({
    key: 'sobLabel',

    displayName:
      'SOB Label',

    text: 'SOB:',

    x: 36,
    y: 881,

    width: 76,
    height: 40,

    fontSize: 6,
    fontWeight: 'bold',

    borderWidth: 1,
  });

  addStaticText({
    key:
      'shipperParticularsNotice',

    displayName:
      'Shipper Particulars Notice',

    text:
      'Particulars above furnished by Shipper / Consignor',

    x: 112,
    y: 881,

    width: 495,
    height: 40,

    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center',

    borderWidth: 1,
  });

  addDynamicField({
    key:
      'documentType',

    displayName:
      'Document Type',

    label:
      'Document Type',

    dataKey:
      'documentType',

    x: 607,
    y: 881,

    width: 170,
    height: 40,

    fontSize: 11,
    fontWeight: 'bold',
    textAlign: 'center',

    showLabel: false,
    borderWidth: 1,
  });

  /*
   * ==========================================
   * FREIGHT ROW
   * ==========================================
   */

  addDynamicField({
    key:
      'freightAmount',

    displayName:
      'Freight Amount',

    label:
      'Freight Amount',

    dataKey:
      'freightAmount',

    x: 36,
    y: 921,

    width: 185,
    height: 56,

    fontSize: 7,

    showLabel: true,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'freightPayableAt',

    displayName:
      'Freight Payable At',

    label:
      'Freight Payable At',

    dataKey:
      'freightPayableAt',

    x: 221,
    y: 921,

    width: 185,
    height: 56,

    fontSize: 7,

    showLabel: true,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'originalBlCount',

    displayName:
      'Original BL Count',

    label:
      'Number of Original BL/MTD (s)',

    dataKey:
      'originalBlCount',

    x: 406,
    y: 921,

    width: 186,
    height: 56,

    fontSize: 7,

    showLabel: true,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'issuePlaceDate',

    displayName:
      'Place and Date of Issue',

    label:
      'Place and Date of issue',

    dataKey:
      'issuePlaceDate',

    x: 592,
    y: 921,

    width: 185,
    height: 56,

    fontSize: 7,

    showLabel: true,
    borderWidth: 1,
  });

  /*
   * ==========================================
   * OTHER PARTICULARS / COMPANY SIGNATURE ROW
   * ==========================================
   */

  addDynamicField({
    key:
      'otherParticulars',

    displayName:
      'Other Particulars',

    label:
      'Other Particulars (if any)',

    dataKey:
      'otherParticulars',

    x: 36,
    y: 977,

    width: 350,
    height: 47,

    fontSize: 7,
    textAlign: 'center',

    showLabel: true,
    borderWidth: 1,
  });

  addDynamicField({
    key:
      'authorisedSignatory',

    displayName:
      'Company Signatory',

    label:
      'Company Signatory',

    dataKey:
      'authorisedSignatory',

    x: 386,
    y: 977,

    width: 391,
    height: 47,

    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',

    showLabel: false,
    borderWidth: 1,
  });

  /*
   * ==========================================
   * FINAL NOTICES
   * ==========================================
   */

  addStaticText({
    key:
      'weightMeasurementNotice',

    displayName:
      'Weight Measurement Notice',

    text:
`Weight and measurement of container not to be included
(TERMS CONTINUED ON BACK)`,

    x: 180,
    y: 1037,

    width: 375,
    height: 32,

    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center',
  });

  addStaticText({
    key:
      'authorisedSignatoryLabel',

    displayName:
      'Authorised Signatory Label',

    text:
      'Authorised Signatory',

    x: 570,
    y: 1040,

    width: 190,
    height: 20,

    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center',
  });

  /*
   * ==========================================
   * FIXED PORTUS FOOTER
   * ==========================================
   */

  add({
    key: 'footer',

    displayName:
      'Footer',

    type: 'text',

    label:
      'Footer',

    dataKey: '',

    previewValue:
      this.fixedFooterText,

    x: 220,
    y: 1095,

    width: 354,
    height: 20,

    fontSize: 7,
    fontWeight: 'normal',
    textAlign: 'center',

    showLabel: false,

    visible: true,
    locked: false,

    borderWidth: 0,

    zIndex: 100,
  });

  addStaticText({
    key: 'pageNumber',

    displayName:
      'Page Number',

    text:
      'page 1 of 1',

    x: 690,
    y: 1096,

    width: 85,
    height: 20,

    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'right',

    zIndex: 101,
  });

  return fields;
}





private enforceFixedFooter(
  source: Record<string, BlFieldConfig>
): Record<string, BlFieldConfig> {
  const defaultFooter =
    this.getDefaultFields().footer;

  const footerSource =
    source.footer ||
    defaultFooter;

  const normalizedFooter =
    this.normalizeField(
      'footer',
      footerSource,
      Object.keys(source).length
    );

  return {
    ...source,

    footer: {
      ...normalizedFooter,

      key: 'footer',
      displayName: 'Footer',
      type: 'text',

      label: 'Footer',
      dataKey: '',
      previewValue:
        this.fixedFooterText,

      showLabel: false,
      visible: true,

      borderWidth: 0,
      borderTopWidth: 0,
      borderRightWidth: 0,
      borderBottomWidth: 0,
      borderLeftWidth: 0,
      zIndex: Math.max(
        Number(normalizedFooter.zIndex || 0),
        100
      ),
    },
  };
}



  private keepAllFieldsInsidePage(): void {
    Object.values(this.fields).forEach((field) => {
      this.keepFieldInsidePage(field);
    });

    this.fields = {
      ...this.fields,
    };
  }

  private keepFieldInsidePage(
    field: BlFieldConfig
  ): void {
    field.width = this.clamp(
      field.width,
      20,
      this.currentPageWidth,
      100
    );

    field.height = this.clamp(
      field.height,
      15,
      this.currentPageHeight,
      30
    );

    field.x = this.clamp(
      field.x,
      0,
      Math.max(
        0,
        this.currentPageWidth - field.width
      ),
      0
    );

    field.y = this.clamp(
      field.y,
      0,
      Math.max(
        0,
        this.currentPageHeight - field.height
      ),
      0
    );
  }

  private async removeExistingDefault(
    currentTemplateId: string
  ): Promise<void> {
    const existingDefaults = this.templates.filter(
      (template) =>
        template.isDefault &&
        template.id !== currentTemplateId
    );

    for (const template of existingDefaults) {
      await setDoc(
        doc(
          this.firestore,
          this.collectionName,
          template.id
        ),
        {
          isDefault: false,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await setDoc(
        doc(this.firestore, this.templateIndexCollectionName, template.id),
        { isDefault: false, updatedAt: serverTimestamp() },
        { merge: true }
      );
    }
  }

  private async resolveCompanyId(): Promise<string> {
  const directKeys = [
    'companyId',
    'selectedCompanyId',
    'currentCompanyId',
    'activeCompanyId',
    'loggedInCompanyId',
  ];

  const objectKeys = [
    'user',
    'currentUser',
    'userData',
    'profile',
    'authUser',
  ];

  const storedCompanyIds =
    new Set<string>();

  let currentUserEmail = '';

  const addCompanyId = (
    value: any
  ): void => {
    const id = String(
      value || ''
    ).trim();

    if (
      id &&
      id !== 'undefined' &&
      id !== 'null'
    ) {
      storedCompanyIds.add(id);
    }
  };

  directKeys.forEach(
    (key) => {
      addCompanyId(
        localStorage.getItem(key)
      );

      addCompanyId(
        sessionStorage.getItem(key)
      );
    }
  );

  objectKeys.forEach(
    (key) => {
      const raw =
        localStorage.getItem(key) ||
        sessionStorage.getItem(key);

      if (!raw) {
        return;
      }

      try {
        const parsed =
          JSON.parse(raw);

        addCompanyId(
          parsed?.companyId
        );

        addCompanyId(
          parsed?.companyKey
        );

        addCompanyId(
          parsed?.company?.id
        );

        addCompanyId(
          parsed?.company?.companyId
        );

        if (!currentUserEmail) {
          currentUserEmail =
            String(
              parsed?.email ||
              parsed?.userEmail ||
              parsed?.companyEmail ||
              ''
            )
              .trim()
              .toLowerCase();
        }
      } catch (error) {
        console.warn(
          `Invalid JSON in "${key}".`,
          error
        );
      }
    }
  );

  const emailDomain = currentUserEmail.includes('@')
    ? (currentUserEmail.split('@').pop() || '').trim().toLowerCase()
    : '';

  const getCompanyDomain = (data: any): string =>
    String(data?.domain || '').trim().toLowerCase()
      .replace(/^@/, '').replace(/^https?:\/\//, '')
      .replace(/^www\./, '').split('/')[0];

  const getCompanyEmailDomain = (data: any): string => {
    const email = String(data?.email || data?.companyEmail || '').trim().toLowerCase();
    return email.includes('@') ? (email.split('@').pop() || '').trim() : '';
  };

  const finalizeCompanyId = (id: string): string => {
    directKeys.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    localStorage.setItem('companyId', id);
    return id;
  };

  // Fast path: one unambiguous cached ID, checked against a real company doc.
  // If its domain conflicts with the logged-in email, preserve the old
  // domain-first scan instead of selecting an unrelated company.
  if (storedCompanyIds.size === 1) {
    const candidateId = Array.from(storedCompanyIds)[0];
    try {
      const candidate = await getDoc(doc(this.firestore, 'companies', candidateId));
      if (candidate.exists()) {
        const data: any = candidate.data() || {};
        const domainMatches = !emailDomain ||
          getCompanyDomain(data) === emailDomain ||
          getCompanyEmailDomain(data) === emailDomain;
        if (domainMatches) {
          return finalizeCompanyId(candidate.id);
        }
      }
    } catch (error) {
      // Network/permission issues: preserve the pre-existing resolution path.
      console.warn('[BL] Direct company lookup failed; trying legacy resolution.', error);
    }
  }

  // Compatibility fallback: retain previous domain-first and single-company
  // logic when local IDs are missing, stale or ambiguous.
  const companiesSnapshot =
    await getDocs(
      collection(
        this.firestore,
        'companies'
      )
    );

  if (
    companiesSnapshot.empty
  ) {
    console.error(
      'No company document exists in Firestore.'
    );

    return '';
  }


  /*
   * User email domain gets priority.
   * This prevents stale IDs from another DB
   * loading the wrong company.
   */
  const domainMatchedCompany =
    emailDomain
      ? companiesSnapshot.docs.find(
          (companyDocument) => {
            const data: any =
              companyDocument.data() ||
              {};

            const companyDomain =
              String(
                data.domain || ''
              )
                .trim()
                .toLowerCase()
                .replace(/^@/, '')
                .replace(
                  /^https?:\/\//,
                  ''
                )
                .replace(/^www\./, '')
                .split('/')[0];

            const companyEmail =
              String(
                data.email ||
                data.companyEmail ||
                ''
              )
                .trim()
                .toLowerCase();

            const companyEmailDomain =
              companyEmail.includes('@')
                ? (
                    companyEmail
                      .split('@')
                      .pop() || ''
                  )
                    .trim()
                    .toLowerCase()
                : '';

            return (
              companyDomain ===
                emailDomain ||
              companyEmailDomain ===
                emailDomain
            );
          }
        )
      : undefined;

  let resolvedCompanyDocument =
    domainMatchedCompany;

  /*
   * Only use stored ID after domain matching.
   */
  if (!resolvedCompanyDocument) {
    resolvedCompanyDocument =
      companiesSnapshot.docs.find(
        (companyDocument) =>
          storedCompanyIds.has(
            companyDocument.id
          )
      );
  }

  if (
    !resolvedCompanyDocument &&
    companiesSnapshot.size === 1
  ) {
    resolvedCompanyDocument =
      companiesSnapshot.docs[0];
  }

  if (!resolvedCompanyDocument) {
    console.error(
      'Company could not be uniquely resolved.',
      {
        currentUserEmail,
        emailDomain,
        storedCompanyIds:
          Array.from(
            storedCompanyIds
          ),
      }
    );

    return '';
  }

  const resolvedCompanyId =
    resolvedCompanyDocument.id;

  /*
   * Remove stale IDs from the previous DB.
   */
  directKeys.forEach(
    (key) => {
      localStorage.removeItem(
        key
      );

      sessionStorage.removeItem(
        key
      );
    }
  );

  localStorage.setItem(
    'companyId',
    resolvedCompanyId
  );

  console.log(
    'BL template company resolved:',
    {
      companyId:
        resolvedCompanyId,

      company:
        resolvedCompanyDocument.data(),
    }
  );

  return resolvedCompanyId;
}






  private getNextTemplateNumber(): number {
    const numbers = this.templates
      .map((template) => {
        const match =
          template.templateName.match(/(\d+)$/);

        return match
          ? Number(match[1])
          : Number.NaN;
      })
      .filter((value) => Number.isFinite(value));

    return numbers.length
      ? Math.max(...numbers) + 1
      : 1;
  }

  private generateTemplateId(): string {
    return (
      `bl_tpl_${Date.now()}_` +
      Math.random().toString(36).slice(2, 8)
    );
  }

  private removeDragListeners(): void {
    document.removeEventListener(
      'mousemove',
      this.onDragMove
    );

    document.removeEventListener(
      'mouseup',
      this.stopDrag
    );
  }

  // VIEW TEMPLATE //

  async viewTemplate(template: BlTemplate): Promise<void> {
  if (!template) return;
  try {
    template = await this.getFullTemplate(template.id);
  } catch (error: any) {
    this.toastr.danger(error?.message || 'Preview could not be loaded.', 'Load Failed');
    return;
  }

  this.viewingTemplate = {
    ...template,
    fields: this.clone(template.fields),
  };

  this.dialogService.open(
    this.templateViewDialog,
    {
      closeOnBackdropClick: true,
      closeOnEsc: true,
      hasBackdrop: true,
      dialogClass: 'bl-template-view-dialog',
      context: {
        template: this.viewingTemplate,
      },
    }
  );
}

private getViewingLayout(): BlViewLayout | null {
  if (!this.viewingTemplate) return null;

  const layouts = this.viewingTemplate.viewLayouts;

  if (layouts) {
    return this.viewMode === 'original'
      ? layouts.original
      : layouts.nonNegotiable;
  }

  return {
    pageWidthPx: this.viewingTemplate.pageWidthPx,
    pageHeightPx: this.viewingTemplate.pageHeightPx,
    globalOffsetX: this.viewingTemplate.globalOffsetX,
    globalOffsetY: this.viewingTemplate.globalOffsetY,
    backgroundImageUrl: this.viewingTemplate.backgroundImageUrl,
    backgroundColor: this.viewingTemplate.backgroundColor,
    textColor: this.viewingTemplate.textColor,
    fields: this.viewingTemplate.fields || {},
  };
}

getViewingFieldKeys(): string[] {
  return Object.keys(this.getViewingLayout()?.fields || {});
}

getViewingPageStyle(): Record<string, string> {
  const layout = this.getViewingLayout();

  if (!layout) return {};

  const backgroundImageUrl =
    this.isOriginalView()
      ? ''
      : String(layout.backgroundImageUrl || '').trim();

  return {
    width: `${layout.pageWidthPx}px`,
    height: `${layout.pageHeightPx}px`,
    backgroundColor: layout.backgroundColor || '#ffffff',
    color: layout.textColor || '#111827',
    backgroundImage: backgroundImageUrl
      ? `url("${backgroundImageUrl}")`
      : 'none',
    backgroundSize: '100% 100%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };
}

getViewingFieldStyle(
  key: string
): Record<string, string> {
  const layout = this.getViewingLayout();
  const field = layout?.fields?.[key];

  if (!layout || !field) return {};

  const offsetX = this.toNumber(layout.globalOffsetX, 0);
  const offsetY = this.toNumber(layout.globalOffsetY, 0);
  const scanBacked =
    !this.isOriginalView() &&
    Boolean(String(layout.backgroundImageUrl || '').trim());

  return {
    left: `${this.toNumber(field.x, 0) + offsetX}px`,
    top: `${this.toNumber(field.y, 0) + offsetY}px`,
    width: `${this.toNumber(field.width, 100)}px`,
    height: `${this.toNumber(field.height, 30)}px`,
    fontSize: `${this.toNumber(field.fontSize, 9)}px`,
    lineHeight: String(this.toNumber(field.lineHeight, 1.25)),
    fontWeight: field.fontWeight || 'normal',
    fontStyle: field.fontStyle || 'normal',
    textDecoration: field.textDecoration || 'none',
    textAlign: field.textAlign || 'left',
    backgroundColor: this.isOriginalView() ? 'transparent' : (field.backgroundColor || 'transparent'),
    color: field.textColor || layout.textColor || '#111827',
    borderTopWidth: `${this.isOriginalView() || scanBacked ? 0 : this.getFieldBorderWidth(field, 'top')}px`,
    borderRightWidth: `${this.isOriginalView() || scanBacked ? 0 : this.getFieldBorderWidth(field, 'right')}px`,
    borderBottomWidth: `${this.isOriginalView() || scanBacked ? 0 : this.getFieldBorderWidth(field, 'bottom')}px`,
    borderLeftWidth: `${this.isOriginalView() || scanBacked ? 0 : this.getFieldBorderWidth(field, 'left')}px`,
    borderStyle: 'solid',
    borderColor: '#111827',
    boxSizing: 'border-box',
    zIndex: String(this.toNumber(field.zIndex, 10)),
    cursor: 'default',
  };
}

get activeTextField(): BlFieldConfig | null {
  const key = this.activeFieldKey;

  if (!key) {
    return null;
  }

  const field = this.fields[key];

  if (
    !field ||
    !field.visible ||
    field.type === 'company-logo'
  ) {
    return null;
  }

  return field;
}

changeActiveFontSize(
  difference: number
): void {
  const field = this.activeTextField;

  if (!field) {
    return;
  }

  const newFontSize =
    this.toNumber(
      field.fontSize,
      9
    ) + difference;

  this.updateField(
    field.key,
    'fontSize',
    newFontSize
  );
}

changeActiveLineHeight(
  difference: number
): void {
  const field = this.activeTextField;

  if (!field) {
    return;
  }

  const currentLineHeight =
    this.toNumber(
      field.lineHeight,
      1.25
    );

  const newLineHeight =
    Math.round(
      (
        currentLineHeight +
        difference
      ) * 100
    ) / 100;

  this.updateField(
    field.key,
    'lineHeight',
    newLineHeight
  );
}

setActiveFontWeight(
  fontWeight: BlFontWeight
): void {
  const field = this.activeTextField;

  if (!field) {
    return;
  }

  this.updateField(
    field.key,
    'fontWeight',
    fontWeight
  );
}

toggleActiveItalic(): void {
  const field = this.activeTextField;

  if (!field) {
    return;
  }

  this.updateField(
    field.key,
    'fontStyle',
    field.fontStyle === 'italic'
      ? 'normal'
      : 'italic'
  );
}

toggleActiveTextDecoration(
  decoration:
    | 'underline'
    | 'line-through'
): void {
  const field = this.activeTextField;

  if (!field) {
    return;
  }

  this.updateField(
    field.key,
    'textDecoration',
    field.textDecoration === decoration
      ? 'none'
      : decoration
  );
}

setActiveTextAlign(
  alignment: BlTextAlign
): void {
  const field = this.activeTextField;

  if (!field) {
    return;
  }

  this.updateField(
    field.key,
    'textAlign',
    alignment
  );
}

toggleActiveBorder(
  side: BlBorderSide
): void {
  const field = this.activeTextField;

  if (!field) {
    return;
  }

  const top =
    this.getFieldBorderWidth(
      field,
      'top'
    );

  const right =
    this.getFieldBorderWidth(
      field,
      'right'
    );

  const bottom =
    this.getFieldBorderWidth(
      field,
      'bottom'
    );

  const left =
    this.getFieldBorderWidth(
      field,
      'left'
    );

  const nextValue =
    this.getFieldBorderWidth(
      field,
      side
    ) > 0
      ? 0
      : 1;

  this.patchActiveField({
    borderTopWidth:
      side === 'top'
        ? nextValue
        : top,

    borderRightWidth:
      side === 'right'
        ? nextValue
        : right,

    borderBottomWidth:
      side === 'bottom'
        ? nextValue
        : bottom,

    borderLeftWidth:
      side === 'left'
        ? nextValue
        : left,
  });
}

setAllActiveBorders(
  enabled: boolean
): void {
  const width =
    enabled ? 1 : 0;

  this.patchActiveField({
    borderWidth: width,
    borderTopWidth: width,
    borderRightWidth: width,
    borderBottomWidth: width,
    borderLeftWidth: width,
  });
}

private patchActiveField(
  changes: Partial<BlFieldConfig>
): void {
  const field =
    this.activeTextField;

  if (!field) {
    return;
  }

  const updatedField: BlFieldConfig = {
    ...field,
    ...changes,
  };

  this.normalizeEditableField(
    updatedField
  );

  this.fields = {
    ...this.fields,
    [field.key]: updatedField,
  };

  this.activeFieldKey =
    field.key;
}


getInlineTypographyStyle():
  Record<string, string> {
  const field = this.activeTextField;

  if (!field) {
    return {};
  }

  const globalOffsetX =
    this.toNumber(
      this.form.globalOffsetX,
      0
    );

  const globalOffsetY =
    this.toNumber(
      this.form.globalOffsetY,
      0
    );

  const toolbarWidth = 560;
const toolbarHeight = 92;

  const gap = 8;

  const fieldLeft =
    this.toNumber(field.x, 0) +
    globalOffsetX;

  const fieldTop =
    this.toNumber(field.y, 0) +
    globalOffsetY;

  const fieldHeight =
    this.toNumber(
      field.height,
      30
    );

  const maximumLeft = Math.max(
    6,
    this.currentPageWidth -
      toolbarWidth -
      6
  );

  const left = this.clamp(
    fieldLeft,
    6,
    maximumLeft,
    6
  );

  let top =
    fieldTop -
    toolbarHeight -
    gap;

  /*
   * If there is no space above the
   * field, show toolbar below it.
   */
  if (top < 6) {
    top =
      fieldTop +
      fieldHeight +
      gap;
  }

  const maximumTop = Math.max(
    6,
    this.currentPageHeight -
      toolbarHeight -
      6
  );

  top = this.clamp(
    top,
    6,
    maximumTop,
    6
  );

  return {
    left: `${left}px`,
    top: `${top}px`,
  };
}

getViewingPreviewValue(
  key: string
): string {
  if (!this.viewingTemplate) {
    return '';
  }

  const field =
    this.getViewingLayout()?.fields?.[key];

  if (!field) {
    return '';
  }

  if (key === 'footer') {
  return this.fixedFooterText;
}



  if (this.isDocumentTypeField(key, field)) {
    return this.getDocumentTypeText();
  }

  if (this.isImportedStructureField(key, field)) {
    return '';
  }

  switch (field.type) {
    case 'company-logo':
      return '';

    case 'container-table':
      return [
        'CONT0012345 | SEAL001 | 1200 KG | 25 | 18.50',
        'CONT0067890 | SEAL002 | 1350 KG | 30 | 20.10',
      ].join('\n');

    case 'company-name':
      return (
        this.company.name ||
        'Company Name'
      );

    case 'company-address':
      return (
        this.company.address ||
        'Company Address'
      );

    case 'company-contact':
      return [
        this.company.telephone
          ? `Tel: ${this.company.telephone}`
          : '',

        this.company.email
          ? `Email: ${this.company.email}`
          : '',

        this.company.ntn
          ? `NTN: ${this.company.ntn}`
          : '',
      ]
        .filter(Boolean)
        .join(' | ');

    case 'text':
      return (
        field.previewValue ||
        field.label ||
        'Static Text'
      );

    case 'field':
    default:
      return (
        String(
          field.previewValue || ''
        ).trim() ||
        (field.dataKey
          ? `{{${field.dataKey}}}`
          : '')
      );
  }
}

  private clamp(
    value: any,
    minimum: number,
    maximum: number,
    fallback: number
  ): number {
    const parsed = Number(value);

    if (!Number.isFinite(parsed)) {
      return fallback;
    }

    return Math.min(
      maximum,
      Math.max(minimum, parsed)
    );
  }

  private toNumber(
    value: any,
    fallback: number
  ): number {
    const parsed = Number(value);

    return Number.isFinite(parsed)
      ? parsed
      : fallback;
  }

  private roundOneDecimal(
    value: number
  ): number {
    return Math.round(value * 10) / 10;
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }
}
